import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatCurrency, formatApiError, API } from "@/lib/api";
import { ArrowLeft, Plus, Trash2, FileText, Star, Download, Printer, Mail, Wrench, FilePlus, ClipboardCheck, Clock, Camera, CheckSquare, CheckCircle2, X as XIcon, Bell, HardHat, Calculator as CalcIcon } from "lucide-react";
import { toast } from "sonner";
import { StatusPill } from "@/pages/Dashboard";
import Documents from "@/components/Documents";
import MaterialTakeoff from "@/components/MaterialTakeoff";
import { ScopePreview } from "@/pages/Deals";
import { formatPhoneDisplay } from "@/lib/format";
import ProjectPhotos from "@/components/ProjectPhotos";
import GrammarCheck from "@/components/GrammarCheck";
import { DealStagePipeline, NextStepCard, DealActivityTimeline } from "@/components/DealWorkflow";
import { InvoiceEditor, EmailInvoiceModal } from "@/pages/Invoices";
import ScopeEditorModal from "@/components/ScopeEditorModal";
import GetAppOnPhoneModal from "@/components/GetAppOnPhoneModal";
import DealSchedulePanel from "@/components/DealSchedulePanel";
import GoToProductionChecklist from "@/components/GoToProductionChecklist";
import ProjectLivePnL from "@/components/ProjectLivePnL";
import DealActionBar from "@/components/DealActionBar";
import CloseOutChecklistModal from "@/components/CloseOutChecklistModal";
import WcscWarrantyModal from "@/components/WcscWarrantyModal";
import FieldAccessCard from "@/components/FieldAccessCard";
import { useAuth } from "@/context/AuthContext";
import CommissionSplitsCard from "@/components/CommissionSplitsCard";
import DealNextAction from "@/components/DealNextAction";
import SubAttendanceCard from "@/components/SubAttendanceCard";

// Resolve a deal's assigned_to_user_id to a human-readable name. Falls back
// to the raw id (handy during dev) and then to "—" when nothing matches.
function assignedRepLabel(deal, users) {
  if (!deal?.assigned_to_user_id) return "—";
  const u = (users || []).find((x) => x.id === deal.assigned_to_user_id);
  return u ? (u.name || u.email || u.id) : "Unknown rep";
}

export default function DealDetail() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { user: currentUser } = useAuth();
  const [deal, setDeal] = useState(null);
  const [contact, setContact] = useState(null);
  const [property, setProperty] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [allUsers, setAllUsers] = useState([]);  // for the Assigned Rep label resolver
  const [options, setOptions] = useState({});
  const [saving, setSaving] = useState(false);
  const [vendorBills, setVendorBills] = useState([]);  // actual bills linked to this project
  const [emailScopeOpen, setEmailScopeOpen] = useState(false);
  const [dealInvoices, setDealInvoices] = useState([]);
  const [dealAssessments, setDealAssessments] = useState([]);
  // One-click + Invoice / Record Payment quick-action modals (live on the Deal page itself)
  const [invoiceEditor, setInvoiceEditor] = useState(null); // null | invoice object (new or existing)
  // Email modal for the "Save & Email" flow off the invoice editor. Kept
  // separate so the editor can close before the email dialog opens.
  const [invoiceEmailTarget, setInvoiceEmailTarget] = useState(null);
  const [scopeEditorOpen, setScopeEditorOpen] = useState(false);
  const [sendToFieldOpen, setSendToFieldOpen] = useState(false);
  const [workOrderOpen, setWorkOrderOpen] = useState(false);
  const [workOrderKind, setWorkOrderKind] = useState("work-order");
  const [finalInvoicePreview, setFinalInvoicePreview] = useState(null); // {contract_total, already_invoiced, final_amount, existing_final_invoice_id?}
  const [closedBannerDismissed, setClosedBannerDismissed] = useState(false);
  const [depositBannerDismissed, setDepositBannerDismissed] = useState(false);
  const [markingComplete, setMarkingComplete] = useState(false);
  const [closeOutModalOpen, setCloseOutModalOpen] = useState(false);
  const [wcscWarrantyOpen, setWcscWarrantyOpen] = useState(false);
  const [draftingDeposit, setDraftingDeposit] = useState(false);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [workOrders, setWorkOrders] = useState([]);
  const [dealEvents, setDealEvents] = useState([]);

  const loadWorkOrders = async () => {
    try {
      const r = await api.get(`/deals/${id}/work-orders`);
      setWorkOrders(Array.isArray(r.data) ? r.data : []);
    } catch {
      setWorkOrders([]);
    }
  };

  // Auto-open the scope editor when arriving from the Calculator's
  // "Open Scope →" button (`/deals/<id>?openScope=1`).
  useEffect(() => {
    if (sp.get("openScope") === "1") {
      setScopeEditorOpen(true);
    }
    if (sp.get("closeout") === "1") {
      setCloseOutModalOpen(true);
    }
    if (sp.get("wcsc") === "1") {
      setWcscWarrantyOpen(true);
    }
  }, [sp]);

  const reload = async () => {
    const r = await api.get(`/deals/${id}`);
    setDeal(r.data);
    if (r.data.contact_id) {
      try { const c = await api.get(`/contacts/${r.data.contact_id}`); setContact(c.data); } catch { setContact(null); }
    } else setContact(null);
    if (r.data.property_id) {
      try { const p = await api.get(`/properties/${r.data.property_id}`); setProperty(p.data); } catch { setProperty(null); }
    } else setProperty(null);
  };

  useEffect(() => {
    reload().catch(() => nav("/projects"));
    api.get("/vendors").then((r) => setVendors(r.data));
    api.get("/options").then((r) => setOptions(r.data));
    api.get(`/vendor-bills?project_id=${id}`).then((r) => setVendorBills(r.data)).catch(() => setVendorBills([]));
    api.get(`/invoices?deal_id=${id}`).then((r) => setDealInvoices(r.data || [])).catch(() => setDealInvoices([]));
    api.get(`/assessments?deal_id=${id}`).then((r) => setDealAssessments(r.data || [])).catch(() => setDealAssessments([]));
    api.get(`/integrations/outlook-invites/status`).then((r) => setGoogleConnected(!!r.data?.connected)).catch(() => setGoogleConnected(false));
    loadWorkOrders();
    api.get(`/deals/${id}/events`).then((r) => setDealEvents(Array.isArray(r.data) ? r.data : [])).catch(() => setDealEvents([]));
  }, [id]);

  // Refresh the Final-invoice preview whenever this deal's status flips, so
  // the Closed-stage suggestion banner can show the projected balance amount.
  useEffect(() => {
    if (!deal) return;
    if (deal.status !== "Closed") {
      setFinalInvoicePreview(null);
      return;
    }
    api.get(`/deals/${id}/final-invoice/preview`)
      .then((r) => setFinalInvoicePreview(r.data))
      .catch(() => setFinalInvoicePreview(null));
  }, [deal?.status, id]);

  // Load the user roster once so we can resolve assigned_to_user_id → name
  // for the badge under the deal title.
  useEffect(() => {
    api.get("/users").then((r) => setAllUsers(r.data || [])).catch(() => setAllUsers([]));
  }, []);

  /**
   * Open the freshly-drafted Final invoice (or the pre-existing one) in the
   * inline InvoiceEditor so the user can review/edit before sending.
   */
  const markCompleteAndInvoice = async () => {
    // Open the Close-Out Checklist modal — the actual finalization happens
    // there once all 16 required items are ticked. The final invoice will be
    // drafted by the modal's onFinalized handler below.
    setCloseOutModalOpen(true);
  };

  const draftFinalInvoiceAfterClose = async () => {
    // Called after the Close-Out modal has archived the deal — drafts the
    // Final invoice using the existing endpoint so the workflow stays
    // consistent with historical behavior. Refreshes the deal so the new
    // "Archived — Won" status + closed_out_at banner appear.
    try {
      const r = await api.post(`/deals/${id}/final-invoice`);
      const newInv = r.data;
      api.get(`/invoices?deal_id=${id}`)
        .then((rr) => setDealInvoices(rr.data || []))
        .catch(() => {});
      toast.success(`Final invoice ${newInv.invoice_number} drafted ($${Number(newInv.total || newInv.total_amount || newInv.subtotal || 0).toLocaleString()})`);
      setInvoiceEditor(newInv);
    } catch (e) {
      // Final invoice may already exist — that's fine.
      const msg = e?.response?.data?.detail || "";
      if (!/already/i.test(msg)) {
        toast.error(msg || "Could not draft Final invoice");
      }
    }
    // Reload deal to reflect Archived status
    api.get(`/deals/${id}`).then((rr) => setDeal(rr.data)).catch(() => {});
  };

  /**
   * Draft (or reopen) the Deposit invoice for this deal at 50% of the contract
   * total, then open the editor for review before sending. Idempotent — if a
   * Deposit invoice already exists, we reload and open it instead.
   */
  const draftDepositInvoice = async (percentage = 50) => {
    if (draftingDeposit) return;
    setDraftingDeposit(true);
    try {
      const r = await api.post(`/deals/${id}/deposit-invoice?percentage=${percentage}`);
      const newInv = r.data;
      // Refresh the deal invoices list so the Money section reflects the new draft
      api.get(`/invoices?deal_id=${id}`)
        .then((rr) => setDealInvoices(rr.data || []))
        .catch(() => { /* non-fatal */ });
      toast.success(`Deposit invoice ${newInv.invoice_number} ready to review`);
      // Pull the full invoice (with fresh line items) and open the editor
      try {
        const full = await api.get(`/invoices/${newInv.id}`);
        setInvoiceEditor(full.data);
      } catch {
        setInvoiceEditor(newInv);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Could not draft deposit invoice");
    } finally {
      setDraftingDeposit(false);
    }
  };

  /**
   * One-click "Mark Paid" — flips an invoice to Paid, sets amount_paid=total,
   * paid_at=today, and fires the GL/journal side-effect. Used on the amber
   * deposit banner (once the invoice is sent) and on each invoice row.
   */
  const markInvoicePaid = async (invoice) => {
    if (!invoice) return;
    const label = invoice.invoice_number || invoice.id;
    if (!window.confirm(`Mark ${label} as PAID? This will post the payment to Books.`)) return;
    try {
      const r = await api.post(`/invoices/${invoice.id}/mark-paid`);
      // Refresh invoices list so the banner disappears and the row status updates.
      api.get(`/invoices?deal_id=${id}`)
        .then((rr) => setDealInvoices(rr.data || []))
        .catch(() => { /* non-fatal */ });
      toast.success(`${r.data.invoice_number} marked Paid`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Could not mark paid");
    }
  };

  /**
   * One-click "Draft Invoice" for a Pending payment milestone.
   * Calls the existing /invoices/from-milestone endpoint, refreshes the
   * invoices list, and opens the editor for review before send.
   */
  const draftMilestoneInvoice = async (milestone) => {
    if (!milestone?.id) {
      toast.error("Save the milestone first (needs an ID)");
      return;
    }
    try {
      const r = await api.post("/invoices/from-milestone", { deal_id: id, milestone_id: milestone.id });
      // Refresh invoices; the milestone status also flips to "Invoiced" server-side.
      api.get(`/invoices?deal_id=${id}`)
        .then((rr) => setDealInvoices(rr.data || []))
        .catch(() => { /* non-fatal */ });
      // Refresh the deal so the milestone status shows the new "Invoiced" state.
      api.get(`/deals/${id}`)
        .then((rr) => setDeal(rr.data))
        .catch(() => { /* non-fatal */ });
      toast.success(`Invoice ${r.data.invoice_number} drafted from milestone`);
      try {
        const full = await api.get(`/invoices/${r.data.id}`);
        setInvoiceEditor(full.data);
      } catch {
        setInvoiceEditor(r.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Could not draft milestone invoice");
    }
  };

  /**
   * Opens the invoice editor pre-filled with contact + property + contract totals.
   * Reused by the Money section header "+ Invoice" button and the DealQuickActions
   * menu (kept for the mobile / non-header path).
   */
  const openNewInvoice = () => {
    const contractTotal = Number(deal.chosen_amount || 0);
    const desc = deal.title ? `${deal.title} — Contract` : "Project Invoice";
    const fullAddr = [property?.address, property?.city, property?.state, property?.zip]
      .filter(Boolean)
      .join(", ");
    setInvoiceEditor({
      deal_id: deal.id,
      customer_contact_id: deal.contact_id || "",
      invoice_type: "Project Amount",
      bill_to_company: property?.name || contact?.company || "",
      bill_to_name: contact?.name || "",
      bill_to_address: property?.address || contact?.address || "",
      bill_to_city: property?.city || contact?.city || "",
      bill_to_state: property?.state || contact?.state || "",
      bill_to_zip: property?.zip || contact?.zip || "",
      bill_to_email: contact?.email || "",
      invoice_date: new Date().toISOString().slice(0, 10),
      terms: "Due Upon Receipt",
      project_title: deal.title || "",
      project_address: fullAddr,
      project_total: contractTotal,
      line_items: contractTotal > 0
        ? [{ description: desc, quantity: 1, unit_price: contractTotal, amount: contractTotal }]
        : [{ description: desc, quantity: 1, unit_price: 0, amount: 0 }],
      status: "Draft",
    });
  };

  /** Opens the oldest unpaid invoice for payment recording. */
  const openRecordPayment = async () => {
    const unpaid = (dealInvoices || []).filter(
      (inv) => !["Paid", "Void"].includes(inv.status) &&
        Number(inv.balance_due || 0) > 0.005
    );
    if (unpaid.length === 0) {
      toast.info(
        "No open invoices on this project yet. Click + Invoice first, save it, then come back to record a payment."
      );
      return;
    }
    const target = [...unpaid].sort((a, b) =>
      String(a.invoice_date || a.created_at || "").localeCompare(
        String(b.invoice_date || b.created_at || "")
      )
    )[0];
    try {
      const full = await api.get(`/invoices/${target.id}`);
      setInvoiceEditor({
        ...full.data,
        payment_date: full.data.payment_date || new Date().toISOString().slice(0, 10),
      });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const totals = useMemo(() => {
    if (!deal) return { revenue: 0, costs: 0, profit: 0, margin: 0, scheduled: 0, received: 0, outstanding: 0, paidCosts: 0, pendingCosts: 0, actualCosts: 0, actualPaid: 0, actualUnpaid: 0, actualProfit: 0, actualMargin: 0 };
    const revenue = Number(deal.chosen_amount || 0);
    const items = deal.cost_items || [];
    const itemsCostsSum = items.reduce((s, i) => s + Number(i.amount || 0), 0);
    const paidCosts = items.filter((i) => i.status === "Paid").reduce((s, i) => s + Number(i.amount || 0), 0);

    // Rolled-up estimate on the deal itself (fallback for deals that don't
    // have granular cost_items yet — mirrors the Live P&L card's
    // Materials / Subcontractor / Labor / Other buckets).
    const rolledUp =
      Number(deal.materials_cost || 0) +
      Number(deal.labor_cost || 0) +
      Number(deal.subcontractor_cost || 0) +
      Number(deal.other_expenses || 0);

    // Client-side back-solve — same math the Live P&L card uses so the two
    // sections agree. Runs only when neither cost_items nor rolled-up
    // estimates exist, i.e. legacy deals priced before we started persisting
    // materials_cost / subcontractor_cost on Set-Option.
    let backSolvedEstimate = 0;
    if (itemsCostsSum <= 0 && rolledUp <= 0 && revenue > 0) {
      const WARRANTY_FIELDS = [
        { yr: 25, opt: "proposal_option_25yr", labor: "labor_25yr_add", oh: "overhead_25yr_pct", pr: "profit_25yr_pct", war: "warranty_25yr_add", hail: "hail_rider_25yr_add" },
        { yr: 20, opt: "proposal_option_1",    labor: "labor_20yr_add", oh: "overhead_20yr_pct", pr: "profit_20yr_pct", war: "warranty_20yr_add", hail: "hail_rider_20yr_add" },
        { yr: 15, opt: "proposal_option_2",    labor: "labor_15yr_add", oh: "overhead_15yr_pct", pr: "profit_15yr_pct", war: "warranty_15yr_add", hail: "" },
        { yr: 10, opt: "proposal_option_3",    labor: "labor_10yr_add", oh: "overhead_10yr_pct", pr: "profit_10yr_pct", war: "warranty_10yr_add", hail: "" },
      ];
      let best = null;
      for (const w of WARRANTY_FIELDS) {
        const price = Number(deal[w.opt] || 0);
        if (price <= 0) continue;
        const diffPct = Math.abs(price - revenue) / revenue;
        if (best === null || diffPct < best.diffPct) best = { w, diffPct };
      }
      if (best && best.diffPct <= 0.20) {
        const w = best.w;
        const ohPct = Number(deal[w.oh] ?? 20);
        const prPct = Number(deal[w.pr] ?? 30);
        const laborAdd = Number(deal[w.labor] || 0);
        const warAdd = Number(deal[w.war] || 0);
        const hailAdd = w.hail ? Number(deal[w.hail] || 0) : 0;
        const base = Math.max(0, revenue - hailAdd - warAdd);
        const mult = (1 + ohPct / 100) * (1 + prPct / 100);
        const subtotal = mult > 0 ? base / mult : 0;
        const matPlusHandling = Math.max(0, subtotal - laborAdd);
        const matEst = matPlusHandling / 1.12;
        backSolvedEstimate = Math.round(matEst + laborAdd);
      }
    }

    // Use the LARGEST of (cost_items sum, rolled-up estimate, back-solved
    // estimate) so we never under-count profit exposure — matches the
    // "safer view" logic in ProjectLivePnL.
    const costs = Math.max(itemsCostsSum, rolledUp, backSolvedEstimate);
    const pendingCosts = Math.max(0, costs - paidCosts);
    const milestones = deal.payment_milestones || [];
    const scheduled = milestones.reduce((s, m) => s + Number(m.amount || 0), 0);
    const received = milestones.filter((m) => m.status === "Paid").reduce((s, m) => s + Number(m.amount || 0), 0);
    const outstanding = scheduled - received;

    // Actual costs from vendor bills — sum line items where project_id matches this project
    let actualCosts = 0;
    let actualPaid = 0;
    let actualUnpaid = 0;
    for (const b of vendorBills) {
      const projectLines = (b.line_items || []).filter((li) => li.project_id === deal.id);
      const lineTotal = projectLines.reduce((s, li) => s + Number(li.amount || 0), 0);
      actualCosts += lineTotal;
      // Paid proportionally — bill paid_amount / total ratio × this project's share
      const billTotal = Number(b.total || 0);
      const paidRatio = billTotal > 0 ? Number(b.paid_amount || 0) / billTotal : 0;
      const paidShare = lineTotal * paidRatio;
      if (b.status === "Paid") {
        actualPaid += lineTotal;
      } else {
        actualPaid += paidShare;
        actualUnpaid += lineTotal - paidShare;
      }
    }
    const actualProfit = revenue - actualCosts;
    const actualMargin = revenue > 0 ? (actualProfit / revenue) * 100 : 0;

    return {
      revenue, costs, profit: revenue - costs,
      margin: revenue > 0 ? ((revenue - costs) / revenue) * 100 : 0,
      scheduled, received, outstanding, paidCosts, pendingCosts,
      actualCosts: Math.round(actualCosts * 100) / 100,
      actualPaid: Math.round(actualPaid * 100) / 100,
      actualUnpaid: Math.round(actualUnpaid * 100) / 100,
      actualProfit: Math.round(actualProfit * 100) / 100,
      actualMargin,
    };
  }, [deal, vendorBills]);

  const persist = async (patch) => {
    if (!deal) return;
    setSaving(true);
    try {
      const body = { ...deal, ...patch };
      // Strip server-managed + server-computed fields ONLY. The `*_cost`
      // fields are user-owned estimates — the backend preserves them when
      // no cost_items exist (see `normalize_deal` in server.py).
      ["id", "created_at", "updated_at", "created_by",
       "total_costs", "profit", "margin_pct",
       "is_deleted", "deleted_at", "deleted_by",
       "assigned_user_name", "primary_contact_name", "property_name"
      ].forEach((k) => { delete body[k]; });
      const r = await api.put(`/deals/${id}`, body);
      setDeal(r.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const applyTemplate = (key) => {
    const tpl = options.milestone_templates?.[key];
    if (!tpl) return;
    if ((deal.payment_milestones || []).length > 0 && !window.confirm("Replace existing milestones?")) return;
    const ms = tpl.map((t) => ({ label: t.label, percent: t.percent, status: "Pending", due_date: "", paid_date: "", notes: "" }));
    persist({ payment_milestones: ms });
  };

  const addMilestone = () => {
    const ms = [...(deal.payment_milestones || []), { label: "", percent: 0, status: "Pending", due_date: "", paid_date: "", notes: "" }];
    persist({ payment_milestones: ms });
  };

  const updateMilestone = (idx, patch) => {
    const ms = [...(deal.payment_milestones || [])];
    ms[idx] = { ...ms[idx], ...patch };
    persist({ payment_milestones: ms });
  };

  const removeMilestone = (idx) => {
    if (!window.confirm("Remove this milestone?")) return;
    const ms = [...(deal.payment_milestones || [])];
    ms.splice(idx, 1);
    persist({ payment_milestones: ms });
  };

  const addCostItem = () => {
    // Blur any in-progress cell edit so pending typed values commit BEFORE we
    // add a new row (otherwise the re-render wipes uncommitted input).
    if (document.activeElement && typeof document.activeElement.blur === "function") {
      document.activeElement.blur();
    }
    // Defer to the next microtask so the blur's onCommit handler runs first
    setTimeout(() => {
      const items = [...(deal?.cost_items || []), { category: "Materials", vendor_id: null, vendor_name: "", description: "", amount: 0, date: "", status: "Pending" }];
      persist({ cost_items: items });
    }, 0);
  };

  const updateCostItem = (idx, patch) => {
    const items = [...(deal.cost_items || [])];
    items[idx] = { ...items[idx], ...patch };
    persist({ cost_items: items });
  };

  const removeCostItem = async (idx) => {
    if (!window.confirm("Remove this cost item?")) return;
    // Compute the new list from the CURRENT deal (this read is synchronous and
    // captures any cell edits that have already committed). Then PUT directly.
    const items = (deal.cost_items || []).filter((_, i) => i !== idx);
    try {
      const r = await api.put(`/deals/${id}`, (() => {
        const body = { ...deal, cost_items: items };
        ["id","created_at","updated_at","created_by",
         "materials_cost","labor_cost","subcontractor_cost","other_expenses_total",
         "total_costs","profit","margin_pct","is_deleted","deleted_at","deleted_by",
         "assigned_user_name","primary_contact_name","property_name"
        ].forEach((k) => delete body[k]);
        return body;
      })());
      setDeal(r.data);
      toast.success("Cost item removed");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message || "Delete failed");
    }
  };

  // ----- Maintenance Plan handlers -----
  const [newVisit, setNewVisit] = useState({ visit_date: new Date().toISOString().slice(0, 10), amount: 0, subcontractor_id: "", notes: "" });
  const subcontractors = useMemo(() => vendors.filter((v) => v.kind === "Subcontractor"), [vendors]);

  const logVisit = async () => {
    if (!newVisit.visit_date) {
      toast.error("Visit date is required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...newVisit, amount: Number(newVisit.amount || 0) };
      if (!payload.subcontractor_id) delete payload.subcontractor_id;
      const r = await api.post(`/deals/${id}/maintenance-visits`, payload);
      setDeal(r.data);
      // Find newest visit
      const visits = r.data?.maintenance_visits || [];
      const newest = [...visits].sort((a, b) => (b.visit_date || "").localeCompare(a.visit_date || ""))[0];
      setNewVisit({ visit_date: new Date().toISOString().slice(0, 10), amount: Number(r.data.maintenance_rate || 0), subcontractor_id: "", notes: "" });
      toast.success("Visit logged — next due date advanced");
      if (newest && Number(payload.amount) > 0 && window.confirm(`Create a draft invoice for $${Number(payload.amount).toLocaleString()}?`)) {
        try {
          const inv = await api.post("/invoices/from-maintenance-visit", { deal_id: id, visit_id: newest.id });
          toast.success(`Draft invoice ${inv.data.invoice_number} created`);
        } catch (e) {
          toast.error("Visit logged, but invoice could not be auto-created");
        }
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const removeVisit = async (visitId) => {
    if (!window.confirm("Remove this visit? Next due date will recalculate.")) return;
    setSaving(true);
    try {
      const r = await api.delete(`/deals/${id}/maintenance-visits/${visitId}`);
      setDeal(r.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  // ----- Change Order handlers -----
  const [newCO, setNewCO] = useState({ description: "", amount: 0, date: new Date().toISOString().slice(0, 10), status: "Approved", notes: "" });

  const addChangeOrder = () => {
    if (!newCO.description.trim()) {
      toast.error("Description is required");
      return;
    }
    const co = { ...newCO, id: crypto.randomUUID(), amount: Number(newCO.amount || 0) };
    const list = [...(deal?.change_orders || []), co];
    persist({ change_orders: list });
    setNewCO({ description: "", amount: 0, date: new Date().toISOString().slice(0, 10), status: "Approved", notes: "" });
  };

  const removeChangeOrder = (coId) => {
    if (!window.confirm("Remove this change order?")) return;
    persist({ change_orders: (deal?.change_orders || []).filter((co) => co.id !== coId) });
  };

  const updateChangeOrder = (coId, patch) => {
    const list = (deal?.change_orders || []).map((co) => (co.id === coId ? { ...co, ...patch } : co));
    persist({ change_orders: list });
  };

  const changeOrderTotal = (deal?.change_orders || [])
    .filter((co) => (co.status || "Approved") === "Approved")
    .reduce((s, co) => s + Number(co.amount || 0), 0);

  if (!deal) return <div className="p-8 text-xs uppercase tracking-[0.2em] text-zinc-500">Loading...</div>;

  const todayStr = new Date().toISOString().slice(0, 10);
  const overdueMilestones = (deal.payment_milestones || []).filter(
    (m) => m.due_date && m.status !== "Paid" && m.due_date < todayStr
  );

  return (
    <div className="p-6 sm:p-8 animate-in fade-in duration-500" data-testid="deal-detail-page">
      <Link to="/projects" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 hover:text-blue-700 mb-4" data-testid="back-to-deals">
        <ArrowLeft className="w-3 h-3" /> Back to Projects
      </Link>

      {/* Closed-stage suggestion: this deal just hit Closed but no Final invoice exists yet. */}
      {deal.status === "Closed"
        && finalInvoicePreview
        && !finalInvoicePreview.existing_final_invoice_id
        && finalInvoicePreview.final_amount > 0
        && !closedBannerDismissed && (
        <div
          className="mb-6 bg-emerald-50 border border-emerald-300 rounded-sm p-4 flex items-start gap-3"
          data-testid="final-invoice-suggestion"
        >
          <CheckSquare className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-emerald-900">This project is Closed — ready to bill?</div>
            <div className="text-xs text-emerald-800 mt-1 leading-snug">
              Contract <b>${finalInvoicePreview.contract_total.toLocaleString()}</b>
              {" "}minus prior invoices <b>${finalInvoicePreview.already_invoiced.toLocaleString()}</b>
              {" "}= <b className="text-emerald-950">${finalInvoicePreview.final_amount.toLocaleString()}</b> remaining balance.
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button
                data-testid="final-invoice-suggest-create"
                onClick={markCompleteAndInvoice}
                disabled={markingComplete}
                className="inline-flex items-center gap-1.5 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 text-white text-[10px] font-bold uppercase tracking-wider px-3 h-8 rounded-sm"
              >
                <CheckSquare className="w-3.5 h-3.5" /> {markingComplete ? "Drafting…" : "Draft Final Invoice"}
              </button>
              <button
                data-testid="final-invoice-suggest-dismiss"
                onClick={() => setClosedBannerDismissed(true)}
                className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 px-2 h-8"
              >
                Not yet
              </button>
            </div>
          </div>
          <button
            onClick={() => setClosedBannerDismissed(true)}
            className="text-emerald-700 hover:text-emerald-900 -mr-1"
            aria-label="Dismiss"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Deposit-invoice suggestion: the deal has a contract total, is past Scope Sent,
          and has no Deposit invoice yet. Encourages a one-click "Review & Send" flow. */}
      {(() => {
        const contractTotal = Number(deal.chosen_amount || 0);
        const POST_SCOPE = new Set(["Won","Deposit Paid","Materials Ordered","Scheduled","In Progress","Final Inspection","Closed","Complete"]);
        const hasDepositInvoice = (dealInvoices || []).some(
          (inv) => inv.invoice_type === "Deposit" && inv.status !== "Void"
        );
        const shouldShow =
          !depositBannerDismissed
          && contractTotal > 0
          && POST_SCOPE.has(deal.status)
          && !hasDepositInvoice;
        if (!shouldShow) return null;
        const depositAmount = Math.round(contractTotal * 0.5 * 100) / 100;
        return (
          <div
            className="mb-6 bg-blue-50 border border-blue-300 rounded-sm p-4 flex items-start gap-3"
            data-testid="deposit-invoice-suggestion"
          >
            <FileText className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-blue-900">Ready to collect a deposit?</div>
              <div className="text-xs text-blue-800 mt-1 leading-snug">
                Contract <b>${contractTotal.toLocaleString()}</b>
                {" · 50% deposit = "}
                <b className="text-blue-950">${depositAmount.toLocaleString()}</b>
                {". "}
                We&apos;ll draft it as a <b>Draft</b> invoice so you can review before sending.
              </div>
              <div className="mt-3 flex items-center gap-2">
                <button
                  data-testid="deposit-invoice-draft-btn"
                  onClick={() => draftDepositInvoice(50)}
                  disabled={draftingDeposit}
                  className="inline-flex items-center gap-1.5 bg-blue-700 hover:bg-blue-800 disabled:opacity-50 text-white text-[10px] font-bold uppercase tracking-wider px-3 h-8 rounded-sm"
                >
                  <FileText className="w-3.5 h-3.5" /> {draftingDeposit ? "Drafting…" : "Draft Deposit Invoice"}
                </button>
                <button
                  data-testid="deposit-invoice-dismiss"
                  onClick={() => setDepositBannerDismissed(true)}
                  className="text-[10px] font-bold uppercase tracking-wider text-blue-700 hover:text-blue-900 px-2 h-8"
                >
                  Not yet
                </button>
              </div>
            </div>
            <button
              onClick={() => setDepositBannerDismissed(true)}
              className="text-blue-700 hover:text-blue-900 -mr-1"
              aria-label="Dismiss"
            >
              <XIcon className="w-4 h-4" />
            </button>
          </div>
        );
      })()}

      {/* Draft-deposit "Review & Send" reminder, plus a "Mark Paid" reminder for
          a Sent-but-unpaid deposit. Keeps the deposit one click away from the top. */}
      {(() => {
        const anyDeposit = (dealInvoices || []).find(
          (inv) => inv.invoice_type === "Deposit" && inv.status !== "Void" && inv.status !== "Paid"
        );
        if (!anyDeposit) return null;
        const isDraft = anyDeposit.status === "Draft";
        return (
          <div
            className="mb-6 bg-amber-50 border border-amber-300 rounded-sm p-4 flex items-start gap-3"
            data-testid="deposit-invoice-review-banner"
          >
            <Mail className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-amber-900">
                {isDraft
                  ? <>Deposit invoice <span className="font-mono">{anyDeposit.invoice_number}</span> is drafted — ready to send?</>
                  : <>Deposit invoice <span className="font-mono">{anyDeposit.invoice_number}</span> sent — awaiting payment</>}
              </div>
              <div className="text-xs text-amber-800 mt-1 leading-snug">
                Amount <b>${Number(anyDeposit.total || 0).toLocaleString()}</b>.
                {" "}
                {isDraft
                  ? "Review and email to the prospect when you're ready."
                  : "When you receive the funds, mark it paid to post the deposit to Books."}
              </div>
              <div className="mt-3 flex items-center gap-2">
                {isDraft && (
                  <button
                    data-testid="deposit-invoice-review-btn"
                    onClick={async () => {
                      try {
                        const full = await api.get(`/invoices/${anyDeposit.id}`);
                        setInvoiceEditor(full.data);
                      } catch {
                        setInvoiceEditor(anyDeposit);
                      }
                    }}
                    className="inline-flex items-center gap-1.5 bg-amber-700 hover:bg-amber-800 text-white text-[10px] font-bold uppercase tracking-wider px-3 h-8 rounded-sm"
                  >
                    <Mail className="w-3.5 h-3.5" /> Review & Send
                  </button>
                )}
                <button
                  data-testid="deposit-invoice-mark-paid-btn"
                  onClick={() => markInvoicePaid(anyDeposit)}
                  className="inline-flex items-center gap-1.5 bg-emerald-700 hover:bg-emerald-800 text-white text-[10px] font-bold uppercase tracking-wider px-3 h-8 rounded-sm"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" /> Mark Paid
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <div className="flex items-start justify-between mb-8 pb-6 border-b border-zinc-200 gap-4 flex-wrap" data-section="overview">
        <div>
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-blue-700">{deal.deal_type || "Scope"}</div>
            <StatusPill status={deal.status} />
            {deal.scope_signed_at && (
              <span data-testid="deal-accepted-badge" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-300 rounded-sm px-2 py-0.5">
                <CheckCircle2 className="w-3 h-3" /> Accepted · signed {String(deal.scope_signed_at).slice(0, 10)}{deal.scope_signed_by_name ? ` by ${deal.scope_signed_by_name}` : ""}
              </span>
            )}
            {saving && <div className="text-[10px] uppercase tracking-wider text-zinc-400">Saving...</div>}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight" data-testid="deal-title">{deal.title}</h1>
          <div className="mt-2 text-xs uppercase tracking-wider text-zinc-500">{deal.lead_source} · {deal.project_type}</div>
          {deal.assigned_to_user_id && (
            <div
              className="mt-1 text-[11px] text-zinc-600"
              data-testid="deal-assigned-rep-badge"
              title="Whoever is shown here owns this project — only an admin can reassign it from the Edit Deal modal."
            >
              <span className="text-zinc-400 uppercase tracking-wider font-bold">Assigned Rep ·</span>{" "}
              <span className="font-medium text-zinc-800">
                {assignedRepLabel(deal, allUsers)}
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <DealActionBar
            deal={deal}
            contact={contact}
            dealAssessments={dealAssessments}
            dealInvoices={dealInvoices}
            onScopeEdit={() => {
              const isConstruction = /^(construction project|other)$/i.test(deal.proposed_roof_type || "") || /other construction work/i.test(deal.current_roof_type || "");
              if (isConstruction) nav(`/deals?edit=${deal.id}`);
              else setScopeEditorOpen(true);
            }}
            onEmailScope={() => setEmailScopeOpen(true)}
            onEmailAssessment={(_assessmentId, variant) => {
              // Assessments module handles this via its editor page today; for
              // now surface the option from here by nav'ing to the editor with
              // an anchor. TODO: expose an inline modal similar to email-scope.
              const a = (dealAssessments || [])[0];
              if (a?.id) nav(`/assessments/${a.id}?email=${variant}`);
            }}
            onWorkOrder={() => { setWorkOrderKind("work-order"); setWorkOrderOpen(true); }}
            onChangeOrder={() => { setWorkOrderKind("change-order"); setWorkOrderOpen(true); }}
            onSendToField={() => setSendToFieldOpen(true)}
            onMarkComplete={markCompleteAndInvoice}
            onOpenWarranty={() => setWcscWarrantyOpen(true)}
            onInvoicesChanged={() => {
              api.get(`/invoices?deal_id=${id}`)
                .then((rr) => setDealInvoices(rr.data || []))
                .catch(() => {});
            }}
            onDealChanged={() => reload().catch(() => {})}
            markingComplete={markingComplete}
          />
        </div>
      </div>

      <DealStagePipeline
        deal={deal}
        invoices={dealInvoices}
        assessments={dealAssessments}
        events={dealEvents}
        onDepositMarkPaid={async (invoice) => {
          // One-click flow from the pipeline pill: mark the deposit invoice
          // Paid (posts journal + fires commission accrual) then refresh
          // both the deal doc and its invoice list so the pill turns green.
          await markInvoicePaid(invoice);
        }}
        onTabChange={(tab) => {
          // Explicit map: each pipeline stage's `tab` value → the stable DOM
          // anchor that already lives on this page. Keeps clicking on the
          // pipeline pills as a smooth "jump to the section that lets me DO
          // this stage" gesture. Falls back to `[data-section="{tab}"]` if
          // the tab isn't in the map (legacy behavior).
          const map = {
            overview:    '[data-section="overview"]',
            scope:       '[data-section="scope"]',
            assessments: '[data-section="documents"]',
            documents:   '[data-section="documents"]',
            milestones:  '[data-section="milestones"]',
            schedule:    '[data-testid="deal-schedule-panel"]',
            work_orders: '[data-section="work_orders"]',
            photos:      '[data-section="photos"]',
            maintenance: '[data-section="maintenance"]',
          };
          const sel = map[tab] || `[data-section="${tab}"]`;
          const el = document.querySelector(sel);
          if (!el) {
            toast.info("That section isn't on this page yet.");
            return;
          }
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          // Flash a blue outline for ~1.4s so the target is unmistakable,
          // then strip the classes so the outline doesn't linger.
          el.classList.add("ring-2", "ring-blue-500", "ring-offset-2");
          setTimeout(() => {
            el.classList.remove("ring-2", "ring-blue-500", "ring-offset-2");
          }, 1400);
        }}
        onAdvance={async (stageKey, becomingDone) => {
          // Manual mark-as-done — limited to status-driven stages
          const map = { won: "Won", in_progress: "In Progress", closed: "Complete" };
          const newStatus = map[stageKey];
          if (!newStatus) {
            toast.info(`"${stageKey}" advances automatically when the underlying data is set.`);
            return;
          }
          if (!becomingDone && deal.status === newStatus) {
            toast.info("Already at this stage.");
            return;
          }
          try {
            await api.put(`/deals/${id}`, { ...deal, status: newStatus });
            toast.success(`Marked ${newStatus}`);
            reload();
          } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || e.message);
          }
        }}
      />

      <FollowUpCard deal={deal} onSaved={reload} />

      <CrewAssignCard deal={deal} />

      <SubAttendanceCard dealId={deal.id} />

      <NextStepCard
        deal={deal}
        invoices={dealInvoices}
        assessments={dealAssessments}
        events={dealEvents}
        onAction={async (step) => {
          if (step.target?.route) {
            nav(step.target.route);
            return;
          }
          if (step.target?.tab) {
            const el = document.querySelector(`[data-section="${step.target.tab}"]`);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            return;
          }
          if (step.action === "email-scope") {
            setEmailScopeOpen(true);
            return;
          }
          if (step.action === "mark-won") {
            try {
              await api.put(`/deals/${id}`, { ...deal, status: "Won" });
              toast.success("Marked Won");
              reload();
            } catch (e) { toast.error(e.message); }
            return;
          }
          if (step.action === "mark-complete") {
            try {
              await api.put(`/deals/${id}`, { ...deal, status: "Complete" });
              toast.success("Project completed");
              reload();
            } catch (e) { toast.error(e.message); }
            return;
          }
          if (step.action === "set-material-order" || step.action === "set-schedule") {
            const el = document.querySelector(`[data-section="schedule"]`);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            else toast.info("Open the project edit modal to set these dates.");
            return;
          }
          toast.info("Action coming soon");
        }}
      />

      {/* --- Quick-jump chips (auto-added by workflow reorder) --- */}
      <div className="sticky top-0 z-20 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-2 mb-4 bg-white/85 backdrop-blur border-b border-zinc-200 flex flex-wrap items-center gap-1.5" data-testid="deal-jump-chips">
        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mr-2">Jump to</span>
        <a href="#deal-group-who" data-testid="jump-chip-who" className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider border border-zinc-300 hover:border-zinc-950 hover:bg-zinc-950 hover:text-white rounded-sm transition-colors">Who</a>
        <a href="#deal-group-scope" data-testid="jump-chip-scope" className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider border border-zinc-300 hover:border-zinc-950 hover:bg-zinc-950 hover:text-white rounded-sm transition-colors">Scope</a>
        <a href="#deal-group-money" data-testid="jump-chip-money" className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider border border-zinc-300 hover:border-zinc-950 hover:bg-zinc-950 hover:text-white rounded-sm transition-colors">Money</a>
        <a href="#deal-group-execution" data-testid="jump-chip-execution" className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider border border-zinc-300 hover:border-zinc-950 hover:bg-zinc-950 hover:text-white rounded-sm transition-colors">Execution</a>
        <a href="#deal-group-history" data-testid="jump-chip-history" className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider border border-zinc-300 hover:border-zinc-950 hover:bg-zinc-950 hover:text-white rounded-sm transition-colors">History</a>
        <Link
          to={`/calculator?deal=${id}`}
          data-testid="jump-chip-calculator"
          className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider border border-blue-700 text-blue-700 bg-blue-50 hover:bg-blue-700 hover:text-white rounded-sm transition-colors inline-flex items-center gap-1"
          title="Open Build Scope pre-linked to this project"
        >
          <CalcIcon className="w-3 h-3" /> Build Scope
        </Link>
        <button
          onClick={async () => {
            try {
              const list = (await api.get(`/custom-estimates?deal_id=${id}`)).data;
              if (list.length) return nav(`/custom-estimates/${list[0].id}`);
              const r = await api.post("/custom-estimates", { title: deal?.title || "", deal_id: id, markup_pct: 30 });
              nav(`/custom-estimates/${r.data.id}`);
            } catch {
              toast.error("Could not open custom estimate");
            }
          }}
          data-testid="jump-chip-custom-estimate"
          className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider border border-emerald-700 text-emerald-700 bg-emerald-50 hover:bg-emerald-700 hover:text-white rounded-sm transition-colors inline-flex items-center gap-1"
          title="Non-roof / mixed-supplier job? Cost it with the Custom Estimate builder (Home Depot, Lowe's, subs, labor)"
        >
          <CalcIcon className="w-3 h-3" /> Custom Estimate
        </button>
        <button
          onClick={async () => {
            try {
              const r = await api.get(`/deals/${id}/sealtech-scope.pdf`, { responseType: "blob" });
              const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
              window.open(url);
              setTimeout(() => URL.revokeObjectURL(url), 60000);
            } catch {
              toast.error("Could not build scope PDF");
            }
          }}
          data-testid="jump-chip-scope-pdf"
          className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider border border-zinc-400 text-zinc-700 hover:border-zinc-950 hover:text-zinc-950 rounded-sm transition-colors inline-flex items-center gap-1"
          title="Printable SealTech Scope of Work & Proposal built from this deal's data"
        >
          <FileText className="w-3 h-3" /> Scope PDF
        </button>
      </div>

      {/* "What's next" — single guided action based on the deal's state */}
      <div className="mt-4">
        <DealNextAction deal={deal} invoices={dealInvoices} onDraftMilestone={draftMilestoneInvoice} />
      </div>

      {/* ═══ GROUP: WHO ═══ */}
      <div id="deal-group-who" data-testid="deal-group-who" className="scroll-mt-16">
      {/* Contact + Property */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <Card title="Contact">
          {contact ? (
            <>
              <Row label="Name" value={contact.contact_name} bold />
              <Row label="Company" value={contact.company_name} />
              <Row label="Phone" value={formatPhoneDisplay(contact.phone)} />
              <Row label="Email" value={contact.email} />
              <Row label="Address" value={contact.address} />
            </>
          ) : (
            <div className="text-sm text-zinc-500">No contact linked.</div>
          )}
        </Card>
        <Card title="Property">
          {property ? (
            <>
              <Row label="Name" value={property.property_name} bold />
              <Row label="Address" value={property.property_address} />
              <Row label="On-Site Contact" value={property.property_contact_name} />
              <Row label="Phone" value={formatPhoneDisplay(property.property_contact_phone)} />
            </>
          ) : (
            <div className="text-sm text-zinc-500">No property linked.</div>
          )}
        </Card>
      </div>

      {deal.notes && (
        <div className="mt-6 bg-white border border-zinc-200 rounded-sm p-6">
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-2">Notes</div>
          <div className="text-sm text-zinc-800 whitespace-pre-wrap" data-testid="deal-notes-view">{deal.notes}</div>
        </div>
      )}

      <div className="mt-6">
        <FieldAccessCard dealId={id} currentUser={currentUser} />
      </div>

      </div>

      {/* ═══ GROUP: SCOPE ═══ */}
      <div id="deal-group-scope" data-testid="deal-group-scope" className="scroll-mt-16">
      {/* Pricing options + spec */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <Card title={(() => {
          const isConstruction = /^(construction project|other)$/i.test(deal.proposed_roof_type || "") || /other construction work/i.test(deal.current_roof_type || "");
          if (isConstruction) return "Project Price";
          return deal.deal_type === "Assessment" ? "Assessment Options" : "Pricing Options";
        })()}>
          {(() => {
            const isConstruction = /^(construction project|other)$/i.test(deal.proposed_roof_type || "") || /other construction work/i.test(deal.current_roof_type || "");
            if (isConstruction) {
              return (
                <Row label="Project Price" value={formatCurrency(deal.proposal_option_1)} bold highlight={Math.abs(totals.revenue - deal.proposal_option_1) < 0.01 && totals.revenue > 0} />
              );
            }
            return (
              <>
                {Number(deal.proposal_option_25yr || 0) > 0 && (
                  <Row label="Option A — 25-yr" value={formatCurrency(deal.proposal_option_25yr)} highlight={Math.abs(totals.revenue - deal.proposal_option_25yr) < 0.01 && totals.revenue > 0} />
                )}
                <Row label="Option B — 20-yr" value={formatCurrency(deal.proposal_option_1)} highlight={Math.abs(totals.revenue - deal.proposal_option_1) < 0.01 && totals.revenue > 0} />
                <Row label="Option C — 15-yr" value={formatCurrency(deal.proposal_option_2)} highlight={Math.abs(totals.revenue - deal.proposal_option_2) < 0.01 && totals.revenue > 0} />
                <Row label="Option D — 10-yr" value={formatCurrency(deal.proposal_option_3)} highlight={Math.abs(totals.revenue - deal.proposal_option_3) < 0.01 && totals.revenue > 0} />
              </>
            );
          })()}
          <div className="border-t-2 border-zinc-950 my-2" />
          <Row label="Chosen" value={formatCurrency(totals.revenue)} bold />
        </Card>

        <Card title="Roof Spec & Measurements" data-section="scope">
          <Row label="Current Roof / Project" value={deal.current_roof_type} />
          <Row label="Proposed Roof / Project" value={deal.proposed_roof_type} bold />
          <div className="pl-1 pb-2">
            <ScopePreview currentRoof={deal.current_roof_type} proposedRoof={deal.proposed_roof_type} />
          </div>
          {(deal.construction_project_requirements || deal.construction_other_requirements || deal.construction_exclusions) && (
            <div id="construction-scope" className="border-t border-zinc-100 mt-2 pt-2 space-y-2" data-testid="deal-detail-construction-scope">
              {deal.project_type_override && (
                <Row label="Project Type (PDF)" value={deal.project_type_override} bold />
              )}
              {deal.construction_project_requirements && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-blue-700 mb-1">Project Requirements</div>
                  <pre className="whitespace-pre-wrap text-xs text-zinc-700 font-sans bg-zinc-50 p-2 border border-zinc-200 rounded-sm">{deal.construction_project_requirements}</pre>
                </div>
              )}
              {deal.construction_other_requirements && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-blue-700 mb-1">Other Requirements</div>
                  <pre className="whitespace-pre-wrap text-xs text-zinc-700 font-sans bg-zinc-50 p-2 border border-zinc-200 rounded-sm">{deal.construction_other_requirements}</pre>
                </div>
              )}
              {deal.construction_exclusions && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-blue-700 mb-1">Exclusions</div>
                  <pre className="whitespace-pre-wrap text-xs text-zinc-700 font-sans bg-zinc-50 p-2 border border-zinc-200 rounded-sm">{deal.construction_exclusions}</pre>
                </div>
              )}
            </div>
          )}
          {deal.custom_scope && !(deal.construction_project_requirements || deal.construction_other_requirements || deal.construction_exclusions) && (
            <div className="border-t border-zinc-100 mt-2 pt-2">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-1">Custom Scope (legacy free-form)</div>
              <pre className="whitespace-pre-wrap text-xs text-zinc-700 font-sans bg-zinc-50 p-2 border border-zinc-200 rounded-sm" data-testid="deal-detail-custom-scope">{deal.custom_scope}</pre>
            </div>
          )}
          <Row label="Project Type" value={deal.project_type} />
          <Row label="Lead Source" value={deal.lead_source} />
          {deal.lead_source === "Referral" && deal.referral_source && (
            <Row label="Referred By" value={deal.referral_source} />
          )}
          {deal.date_sent && <Row label="Date Sent" value={deal.date_sent} />}
          {deal.chosen_date && <Row label="Chosen Date" value={deal.chosen_date} />}
          {(deal.property_sqft || deal.perimeter_lnft || deal.avg_parapet_height) ? (
            <>
              <div className="border-t border-zinc-100 my-2" />
              <Row label="Property SqFt" value={deal.property_sqft ? Number(deal.property_sqft).toLocaleString() : "—"} />
              <Row label="Perimeter LnFt" value={deal.perimeter_lnft ? Number(deal.perimeter_lnft).toLocaleString() : "—"} />
              <Row label="Avg Parapet Ht (ft)" value={deal.avg_parapet_height || "—"} />
              <Row label="Total SqFt" value={deal.total_sqft ? Number(deal.total_sqft).toLocaleString() : "—"} bold />
            </>
          ) : null}
        </Card>
      </div>

      {/* Documents */}
      <div data-section="documents">
      <Documents
        parentType="project"
        parentId={id}
        title="Documents — Measurement Reports, Assessments, Scopes, Invoices, Photos"
        coverPhotoId={deal.cover_photo_file_id}
        onSetCover={(fileId) => persist({ cover_photo_file_id: deal.cover_photo_file_id === fileId ? null : fileId })}
      />
      </div>
      {/* Material Take-Off */}
      <MaterialTakeoff deal={deal} reload={reload} />

      </div>

      {/* ═══ GROUP: MONEY ═══ */}
      <div id="deal-group-money" data-testid="deal-group-money" className="scroll-mt-16">
      {/* Money section header — quick actions for adding invoices and recording payments */}
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap" data-testid="money-section-header">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-blue-700 mb-1">Money</div>
          <h2 className="font-heading text-xl font-bold tracking-tight">Financials, invoices & payments</h2>
        </div>
        <div className="flex items-center gap-2">
          {overdueMilestones.length > 0 && (
            <button
              onClick={() => document.querySelector('[data-section="milestones"]')?.scrollIntoView({ behavior: "smooth", block: "center" })}
              data-testid="overdue-milestones-bell"
              title={overdueMilestones.map((m) => `${m.label || "Milestone"} — due ${m.due_date}`).join("\n")}
              className="inline-flex items-center gap-1.5 bg-red-50 border border-red-300 text-red-800 px-3 h-9 text-[10px] font-bold uppercase tracking-wider rounded-sm hover:bg-red-100 transition-colors animate-in fade-in"
            >
              <Bell className="w-3.5 h-3.5" /> {overdueMilestones.length} Overdue Milestone{overdueMilestones.length !== 1 ? "s" : ""}
            </button>
          )}
          <button
            onClick={openNewInvoice}
            data-testid="money-new-invoice-btn"
            className="inline-flex items-center gap-1.5 border border-zinc-300 hover:border-zinc-950 text-zinc-800 px-3 h-9 text-[10px] font-bold uppercase tracking-wider rounded-sm transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Invoice
          </button>
          <button
            onClick={openRecordPayment}
            data-testid="money-record-payment-btn"
            className="inline-flex items-center gap-1.5 bg-emerald-700 hover:bg-emerald-800 text-white px-3 h-9 text-[10px] font-bold uppercase tracking-wider rounded-sm transition-colors"
          >
            <CheckCircle2 className="w-3.5 h-3.5" /> Record Payment
          </button>
        </div>
      </div>
      {/* Live Project P&L — top of the Money section, answers "what did we make?" at a glance */}
      <ProjectLivePnL deal={deal} dealInvoices={dealInvoices} vendorBills={vendorBills} onSave={persist} />

      {/* Financials KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KpiCard label="Revenue" value={formatCurrency(totals.revenue)} testId="kpi-revenue" />
        <KpiCard label="Total Costs" value={formatCurrency(totals.costs)} hint={`${formatCurrency(totals.paidCosts)} paid · ${formatCurrency(totals.pendingCosts)} pending`} testId="kpi-costs" />
        <KpiCard label="Net Profit" value={formatCurrency(totals.profit)} hint={`Margin ${totals.margin.toFixed(1)}%`} accent={totals.profit >= 0 ? "text-emerald-700" : "text-red-700"} testId="kpi-profit" />
        <KpiCard label="Outstanding" value={formatCurrency(totals.outstanding)} hint={`${formatCurrency(totals.received)} received of ${formatCurrency(totals.scheduled)}`} accent="text-orange-700" testId="kpi-outstanding" />
      </div>

      {/* Invoices list — click a row to open the editor and see payment details. */}
      {dealInvoices.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-sm p-5 mb-8" data-testid="deal-invoices-section">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-blue-700">Invoices on this project</div>
              <h3 className="font-heading text-lg font-bold tracking-tight">{dealInvoices.length} invoice{dealInvoices.length !== 1 ? "s" : ""}</h3>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-zinc-950 text-left text-[10px] uppercase tracking-wider text-zinc-500">
                <th className="py-2">Invoice #</th>
                <th className="py-2">Type</th>
                <th className="py-2">Status</th>
                <th className="py-2 text-right">Total</th>
                <th className="py-2 text-right">Received</th>
                <th className="py-2 text-right">Balance</th>
                <th className="py-2">Paid On</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {[...dealInvoices]
                .sort((a, b) => String(b.invoice_date || b.created_at || "").localeCompare(String(a.invoice_date || a.created_at || "")))
                .map((inv) => {
                  const statusColor = {
                    Paid: "bg-emerald-100 text-emerald-800",
                    Sent: "bg-blue-100 text-blue-800",
                    Partial: "bg-amber-100 text-amber-800",
                    Overdue: "bg-red-100 text-red-800",
                    Draft: "bg-zinc-200 text-zinc-700",
                    Void: "bg-zinc-100 text-zinc-400 line-through",
                  }[inv.status] || "bg-zinc-100 text-zinc-700";
                  return (
                    <tr
                      key={inv.id}
                      className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                      data-testid={`deal-invoice-row-${inv.id}`}
                      onClick={async () => {
                        try {
                          const full = await api.get(`/invoices/${inv.id}`);
                          setInvoiceEditor(full.data);
                        } catch (e) {
                          toast.error(formatApiError(e?.response?.data?.detail) || e.message);
                        }
                      }}
                    >
                      <td className="py-3 font-mono font-bold">{inv.invoice_number}</td>
                      <td className="py-3 text-xs">{inv.invoice_type || "—"}</td>
                      <td className="py-3"><span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${statusColor}`}>{inv.status}</span></td>
                      <td className="py-3 text-right font-mono">{formatCurrency(inv.total || 0)}</td>
                      <td className="py-3 text-right font-mono text-emerald-700">{formatCurrency(inv.amount_paid || 0)}</td>
                      <td className={`py-3 text-right font-mono font-bold ${Number(inv.balance_due || 0) > 0.005 ? "text-orange-700" : "text-zinc-400"}`}>
                        {formatCurrency(inv.balance_due || 0)}
                      </td>
                      <td className="py-3 text-xs text-zinc-600">
                        {inv.payment_date
                          ? <span>{inv.payment_date} {inv.payment_method ? <span className="text-zinc-400">· {inv.payment_method}</span> : null}</span>
                          : <span className="text-zinc-300">—</span>}
                      </td>
                      <td className="py-3 text-right text-[10px] font-bold uppercase tracking-wider text-blue-700">View →</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}

      {/* Milestones */}
      <ActualLaborCard dealId={deal.id} estLabor={Number(deal.labor_cost || 0)} />
      <Card title="Payment Milestones" data-section="milestones" right={
        <div className="flex flex-wrap items-center gap-2">
          {Object.keys(options.milestone_templates || {}).map((k) => (
            <button key={k} data-testid={`milestone-template-${k.replace(/\//g, "-")}`} onClick={() => applyTemplate(k)} className="px-3 h-8 text-[10px] font-bold uppercase tracking-wider border border-zinc-300 hover:border-zinc-950 rounded-sm">
              {k}
            </button>
          ))}
          <button data-testid="add-milestone" onClick={addMilestone} className="inline-flex items-center gap-1 px-3 h-8 text-[10px] font-bold uppercase tracking-wider bg-blue-700 text-white hover:bg-blue-800 rounded-sm">
            <Plus className="w-3 h-3" /> Custom
          </button>
        </div>
      }>
        {(deal.payment_milestones || []).length === 0 ? (
          <div className="text-sm text-zinc-500 py-6 text-center">No milestones yet. Pick a template (50/50 or 50/25/25) or add custom.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="milestones-table">
              <thead>
                <tr className="border-b-2 border-zinc-950 text-left text-[10px] uppercase tracking-wider">
                  <th className="py-2 pr-3">Label</th>
                  <th className="py-2 pr-3 w-20 text-right">%</th>
                  <th className="py-2 pr-3 w-32 text-right">Amount</th>
                  <th className="py-2 pr-3 w-36">Due Date</th>
                  <th className="py-2 pr-3 w-36">Status</th>
                  <th className="py-2 pr-3 w-36">Paid Date</th>
                  <th className="py-2 pr-3 w-36 text-center">Invoice</th>
                  <th className="py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {deal.payment_milestones.map((m, i) => {
                  // Find any invoice linked to this milestone (source_type=milestone,
                  // source_id=m.id) so we can show "Open" instead of "Draft".
                  const linkedInv = (dealInvoices || []).find(
                    (inv) => inv.source_type === "milestone" && inv.source_id === m.id
                  );
                  return (
                  <tr key={m.id || i} className="border-b border-zinc-100" data-testid={`milestone-row-${i}`}>
                    <td className="py-2 pr-3">
                      <CellInput value={m.label} onCommit={(v) => updateMilestone(i, { label: v })} placeholder="Deposit / Mid-Job / Completion" data-testid={`milestone-label-${i}`} />
                    </td>
                    <td className="py-2 pr-3 text-right">
                      <CellInput type="number" step="5" min="0" max="100" value={m.percent} onCommit={(v) => updateMilestone(i, { percent: parseFloat(v || 0) })} className="text-right" data-testid={`milestone-percent-${i}`} />
                    </td>
                    <td className="py-2 pr-3 text-right font-mono">{formatCurrency(m.amount)}</td>
                    <td className="py-2 pr-3">
                      <CellInput type="date" value={m.due_date} onCommit={(v) => updateMilestone(i, { due_date: v })} data-testid={`milestone-due-${i}`} />
                      {m.due_date && m.status !== "Paid" && m.due_date < todayStr && (
                        <div className="text-[9px] font-bold uppercase tracking-wider text-red-700 mt-0.5 inline-flex items-center gap-1" data-testid={`milestone-overdue-${i}`}>
                          <Bell className="w-2.5 h-2.5" /> Overdue
                        </div>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <CellSelect value={m.status} onCommit={(v) => updateMilestone(i, { status: v, paid_date: v === "Paid" && !m.paid_date ? new Date().toISOString().slice(0, 10) : m.paid_date })} options={options.milestone_statuses || ["Pending", "Invoiced", "Paid"]} data-testid={`milestone-status-${i}`} />
                    </td>
                    <td className="py-2 pr-3">
                      <CellInput type="date" value={m.paid_date} onCommit={(v) => updateMilestone(i, { paid_date: v })} disabled={m.status !== "Paid"} data-testid={`milestone-paid-${i}`} />
                    </td>
                    <td className="py-2 pr-3 text-center">
                      {linkedInv ? (
                        <button
                          onClick={async () => {
                            try {
                              const full = await api.get(`/invoices/${linkedInv.id}`);
                              setInvoiceEditor(full.data);
                            } catch {
                              setInvoiceEditor(linkedInv);
                            }
                          }}
                          className="inline-flex items-center gap-1 px-2 h-7 text-[9px] font-bold uppercase tracking-wider border border-zinc-300 hover:border-zinc-950 rounded-sm"
                          data-testid={`milestone-open-invoice-${i}`}
                          title={`${linkedInv.invoice_number} · ${linkedInv.status}`}
                        >
                          <FileText className="w-3 h-3" /> {linkedInv.invoice_number.split("-").pop()}
                        </button>
                      ) : (
                        <button
                          onClick={() => draftMilestoneInvoice(m)}
                          disabled={!m.id || Number(m.amount || 0) <= 0}
                          className="inline-flex items-center gap-1 px-2 h-7 text-[9px] font-bold uppercase tracking-wider bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-40 rounded-sm"
                          data-testid={`milestone-draft-invoice-${i}`}
                        >
                          <FilePlus className="w-3 h-3" /> Draft
                        </button>
                      )}
                    </td>
                    <td className="py-2 text-right">
                      <button onClick={() => removeMilestone(i)} className="p-1.5 hover:bg-red-100 text-red-700 rounded-sm" data-testid={`milestone-delete-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-zinc-950">
                  <td className="py-2 pr-3 font-bold uppercase text-[10px] tracking-wider">Total Scheduled</td>
                  <td className="py-2 pr-3 text-right font-mono text-zinc-500 text-xs">{deal.payment_milestones.reduce((s, m) => s + Number(m.percent || 0), 0)}%</td>
                  <td className="py-2 pr-3 text-right font-mono font-bold">{formatCurrency(totals.scheduled)}</td>
                  <td colSpan={5}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>

      {/* Commission split — admin only (backend PUT also enforces admin) */}
      {currentUser?.role === "admin" && (
        <CommissionSplitsCard dealId={id} users={allUsers} />
      )}

      {/* Cost Items */}
      <Card title="Vendor Cost Line Items" right={
        <div className="flex items-center gap-2">
          <Link to={`/calculator?deal=${deal.id}`} className="inline-flex items-center gap-1 px-3 h-8 text-[10px] font-bold uppercase tracking-wider border border-blue-700 text-blue-700 bg-white hover:bg-blue-50 rounded-sm" data-testid="pull-from-calculator">
            <FileText className="w-3 h-3" /> Pull from Calculator
          </Link>
          <button data-testid="add-cost-item" onClick={addCostItem} className="inline-flex items-center gap-1 px-3 h-8 text-[10px] font-bold uppercase tracking-wider bg-blue-700 text-white hover:bg-blue-800 rounded-sm">
            <Plus className="w-3 h-3" /> Add Line
          </button>
        </div>
      }>
        {(deal.cost_items || []).length === 0 ? (
          <div className="text-sm text-zinc-500 py-6 text-center">No cost items yet. Add materials, labor, or sub payments as they occur.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cost-items-table">
              <thead>
                <tr className="border-b-2 border-zinc-950 text-left text-[10px] uppercase tracking-wider">
                  <th className="py-2 pr-3 w-32">Category</th>
                  <th className="py-2 pr-3 w-44">Vendor</th>
                  <th className="py-2 pr-3">Description</th>
                  <th className="py-2 pr-3 w-32 text-right">Amount</th>
                  <th className="py-2 pr-3 w-36">Date</th>
                  <th className="py-2 pr-3 w-28">Status</th>
                  <th className="py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {deal.cost_items.map((c, i) => (
                  <tr key={c.id || i} className="border-b border-zinc-100" data-testid={`cost-row-${i}`}>
                    <td className="py-2 pr-3">
                      <CellSelect value={c.category} onCommit={(v) => updateCostItem(i, { category: v })} options={options.cost_categories || ["Materials", "Labor", "Subcontractor", "Other"]} data-testid={`cost-category-${i}`} />
                    </td>
                    <td className="py-2 pr-3">
                      <CellSelect
                        value={c.vendor_id || "__free__"}
                        onCommit={(v) => {
                          if (v === "__free__") return updateCostItem(i, { vendor_id: null });
                          const vendor = vendors.find((x) => x.id === v);
                          updateCostItem(i, { vendor_id: v, vendor_name: vendor?.name || "" });
                        }}
                        options={[{ value: "__free__", label: c.vendor_name || "— Pick / Custom —" }, ...vendors.map((v) => ({ value: v.id, label: v.name }))]}
                        data-testid={`cost-vendor-${i}`}
                      />
                      {!c.vendor_id && (
                        <CellInput value={c.vendor_name} onCommit={(v) => updateCostItem(i, { vendor_name: v })} placeholder="Vendor name" className="mt-1 text-xs" data-testid={`cost-vendor-name-${i}`} />
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <CellInput value={c.description} onCommit={(v) => updateCostItem(i, { description: v })} placeholder="What was this for?" data-testid={`cost-description-${i}`} />
                    </td>
                    <td className="py-2 pr-3 text-right">
                      <CellInput type="number" value={c.amount} onCommit={(v) => updateCostItem(i, { amount: parseFloat(v || 0) })} className="text-right font-mono" data-testid={`cost-amount-${i}`} />
                    </td>
                    <td className="py-2 pr-3">
                      <CellInput type="date" value={c.date} onCommit={(v) => updateCostItem(i, { date: v })} data-testid={`cost-date-${i}`} />
                    </td>
                    <td className="py-2 pr-3">
                      <CellSelect value={c.status} onCommit={(v) => updateCostItem(i, { status: v })} options={options.cost_item_statuses || ["Pending", "Paid"]} data-testid={`cost-status-${i}`} />
                    </td>
                    <td className="py-2 text-right">
                      <button onClick={() => removeCostItem(i)} className="p-1.5 hover:bg-red-100 text-red-700 rounded-sm" data-testid={`cost-delete-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-zinc-950">
                  <td className="py-2 pr-3 font-bold uppercase text-[10px] tracking-wider" colSpan={3}>Total Costs (auto-rolls into Dashboard)</td>
                  <td className="py-2 pr-3 text-right font-mono font-bold">{formatCurrency(totals.costs)}</td>
                  <td colSpan={3}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-4 border-t border-zinc-100">
          <Mini label="Materials" value={formatCurrency(deal.materials_cost)} />
          <Mini label="Labor" value={formatCurrency(deal.labor_cost)} />
          <Mini label="Subcontractor" value={formatCurrency(deal.subcontractor_cost)} />
          <Mini label="Other" value={formatCurrency(deal.other_expenses)} />
        </div>
      </Card>

      {/* Estimated vs Actual P&L */}
      <div className="bg-white border border-zinc-200 rounded-sm p-5 mb-8" data-testid="pnl-comparison">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-blue-700">P&amp;L Comparison</div>
            <h3 className="font-heading text-lg font-bold tracking-tight">Estimated vs Actual</h3>
          </div>
          <Link to={`/payables?project=${id}`} className="text-[10px] font-bold uppercase tracking-wider text-blue-700 hover:underline">
            View Vendor Bills ({vendorBills.length}) →
          </Link>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-zinc-950 text-left text-[10px] uppercase tracking-wider">
              <th className="py-2"></th>
              <th className="py-2 text-right">Estimated (Cost Items)</th>
              <th className="py-2 text-right">Actual (Vendor Bills)</th>
              <th className="py-2 text-right">Variance</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-zinc-100">
              <td className="py-3 font-bold">Revenue</td>
              <td className="py-3 text-right font-mono">{formatCurrency(totals.revenue)}</td>
              <td className="py-3 text-right font-mono">{formatCurrency(totals.revenue)}</td>
              <td className="py-3 text-right font-mono text-zinc-400">—</td>
            </tr>
            <tr className="border-b border-zinc-100">
              <td className="py-3 font-bold">Costs</td>
              <td className="py-3 text-right font-mono">{formatCurrency(totals.costs)}</td>
              <td className="py-3 text-right font-mono">{formatCurrency(totals.actualCosts)}</td>
              <td className={`py-3 text-right font-mono font-bold ${totals.actualCosts > totals.costs ? "text-red-700" : "text-emerald-700"}`}>
                {totals.actualCosts > totals.costs ? "+" : ""}{formatCurrency(totals.actualCosts - totals.costs)}
              </td>
            </tr>
            <tr className="border-b-2 border-zinc-950 bg-zinc-50">
              <td className="py-3 font-bold">Net Profit</td>
              <td className={`py-3 text-right font-mono font-bold ${totals.profit >= 0 ? "text-emerald-700" : "text-red-700"}`}>{formatCurrency(totals.profit)}</td>
              <td className={`py-3 text-right font-mono font-bold ${totals.actualProfit >= 0 ? "text-emerald-700" : "text-red-700"}`}>{formatCurrency(totals.actualProfit)}</td>
              <td className={`py-3 text-right font-mono font-bold ${totals.actualProfit >= totals.profit ? "text-emerald-700" : "text-red-700"}`}>
                {totals.actualProfit >= totals.profit ? "+" : ""}{formatCurrency(totals.actualProfit - totals.profit)}
              </td>
            </tr>
            <tr>
              <td className="py-2 text-[11px] uppercase tracking-wider text-zinc-500">Margin</td>
              <td className="py-2 text-right font-mono text-[11px] text-zinc-600">{totals.margin.toFixed(1)}%</td>
              <td className="py-2 text-right font-mono text-[11px] text-zinc-600">{totals.actualMargin.toFixed(1)}%</td>
              <td className="py-2"></td>
            </tr>
          </tbody>
        </table>
        {vendorBills.length === 0 ? (
          <div className="mt-3 text-[11px] text-zinc-500 italic">No vendor bills attached to this project yet. Upload one on the Payables page and link a line item to this project to see actuals here.</div>
        ) : (
          <div className="mt-3 text-[11px] text-zinc-500">
            {vendorBills.length} bill{vendorBills.length > 1 ? "s" : ""} attached · {formatCurrency(totals.actualPaid)} paid · {formatCurrency(totals.actualUnpaid)} unpaid
          </div>
        )}
      </div>

      </div>

      {/* ═══ GROUP: EXECUTION ═══ */}
      <div id="deal-group-execution" data-testid="deal-group-execution" className="scroll-mt-16">
      {/* Go to Production — one-card checklist of the 4 things to do when a deal is Won */}
      <GoToProductionChecklist
        deal={deal}
        events={dealEvents}
        onScrollToSchedule={() => {
          const el = document.querySelector('[data-testid="deal-schedule-panel"]') || document.getElementById("deal-group-execution");
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }}
        onScrollToMaterials={() => {
          const el = document.querySelector('[data-testid="takeoff-card"]') || document.getElementById("deal-group-scope");
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }}
        onSendWorkOrder={() => { setWorkOrderKind("work-order"); setWorkOrderOpen(true); }}
        onEquipmentChange={(next) => setDeal((d) => (d ? { ...d, equipment_ordered: next } : d))}
      />

      {/* Schedule / Events panel — ad-hoc appointments tied to this deal */}
      <DealSchedulePanel
        dealId={id}
        googleConnected={googleConnected}
        onEventsChange={(next) => setDealEvents(next || [])}
      />

      {/* Work Orders — every WO + Change Order sent from this deal, with
          sent/signed status and one-tap PDF download. Anchors the pipeline
          "WO Sent" and "WO Signed" pills. */}
      <Card
        data-section="work_orders"
        title={
          <span className="inline-flex items-center gap-2">
            <FileText className="w-3.5 h-3.5 text-blue-700" /> Work Orders & Change Orders
            {workOrders.some((w) => w.status === "signed") && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm">
                {workOrders.filter((w) => w.status === "signed").length} signed
              </span>
            )}
          </span>
        }
        right={
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setWorkOrderKind("work-order"); setWorkOrderOpen(true); }}
              className="inline-flex items-center gap-1 h-8 px-3 text-[10px] font-bold uppercase tracking-wider border border-zinc-300 hover:border-blue-700 hover:text-blue-700 rounded-sm"
              data-testid="deal-send-wo-btn"
            >
              <Plus className="w-3 h-3" /> Send WO
            </button>
            <button
              onClick={() => { setWorkOrderKind("change-order"); setWorkOrderOpen(true); }}
              className="inline-flex items-center gap-1 h-8 px-3 text-[10px] font-bold uppercase tracking-wider border border-zinc-300 hover:border-blue-700 hover:text-blue-700 rounded-sm"
              data-testid="deal-send-co-btn"
            >
              <FilePlus className="w-3 h-3" /> Send CO
            </button>
          </div>
        }
      >
        {workOrders.length === 0 ? (
          <div className="text-sm text-zinc-500 py-3 text-center italic">
            No Work Orders sent yet. Click <b>Send WO</b> above to email a signable Work Order to your subcontractor.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="deal-work-orders-table">
              <thead>
                <tr className="border-b-2 border-zinc-950 text-left text-[10px] uppercase tracking-wider">
                  <th className="py-2 pr-3 w-24">Type</th>
                  <th className="py-2 pr-3">Sub / Contact</th>
                  <th className="py-2 pr-3 w-32">Sent</th>
                  <th className="py-2 pr-3 w-40">Signed</th>
                  <th className="py-2 pr-3 w-24 text-right">Amount</th>
                  <th className="py-2 pr-3 w-28">Status</th>
                  <th className="py-2 w-16"></th>
                </tr>
              </thead>
              <tbody>
                {workOrders.map((w) => {
                  const isCO = w.kind === "change-order";
                  const isSigned = w.status === "signed";
                  const pdfHref = `${process.env.REACT_APP_BACKEND_URL}/api/deals/${id}/work-order/pdf?kind=${w.kind || "work-order"}`;
                  return (
                    <tr key={w.id} className="border-b border-zinc-100" data-testid={`wo-row-${w.id}`}>
                      <td className="py-2 pr-3">
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${isCO ? "bg-purple-50 text-purple-700 border border-purple-200" : "bg-blue-50 text-blue-700 border border-blue-200"}`}>
                          {isCO ? "Change Order" : "Work Order"}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-zinc-700">
                        <div className="font-semibold">{w.sub_company || w.contractor || "—"}</div>
                        {w.sub_contact && <div className="text-[11px] text-zinc-500">{w.sub_contact} · {w.sub_email}</div>}
                      </td>
                      <td className="py-2 pr-3 font-mono text-[11px] text-zinc-600">
                        {w.sent_at ? new Date(w.sent_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="py-2 pr-3">
                        {isSigned ? (
                          <div className="text-[11px]">
                            <div className="font-semibold text-emerald-700 flex items-center gap-1">
                              <CheckCircle2 className="w-3.5 h-3.5" /> {w.signed_by_name || "Signed"}
                            </div>
                            <div className="font-mono text-[10px] text-zinc-500">
                              {w.signed_at ? new Date(w.signed_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}
                            </div>
                          </div>
                        ) : (
                          <span className="text-[11px] text-amber-700 italic">Awaiting signature</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-right font-mono font-bold">{formatCurrency(w.total)}</td>
                      <td className="py-2 pr-3">
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm ${
                          isSigned ? "bg-emerald-50 text-emerald-700 border border-emerald-300" :
                          w.status === "sent" ? "bg-amber-50 text-amber-800 border border-amber-300" :
                          "bg-zinc-50 text-zinc-600 border border-zinc-300"
                        }`}>
                          {w.status || "draft"}
                        </span>
                      </td>
                      <td className="py-2 text-right">
                        <a
                          href={pdfHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2 h-7 text-[10px] font-bold uppercase tracking-wider border border-zinc-300 hover:border-blue-700 hover:text-blue-700 rounded-sm"
                          data-testid={`wo-pdf-${w.id}`}
                          title="Open the PDF"
                        >
                          <Download className="w-3 h-3" /> PDF
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Change Orders */}
      <Card
        title={
          <span className="inline-flex items-center gap-2">
            <FilePlus className="w-3.5 h-3.5 text-blue-700" /> Change Orders
            {changeOrderTotal !== 0 && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-sm">
                +{formatCurrency(changeOrderTotal)} approved
              </span>
            )}
          </span>
        }
      >
        {/* Add new change order */}
        <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-3 mb-4">
          <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-2">Add Change Order</div>
          <div className="grid grid-cols-1 sm:grid-cols-6 gap-2">
            <input
              type="date"
              value={newCO.date}
              onChange={(e) => setNewCO({ ...newCO, date: e.target.value })}
              className="h-9 px-2 border border-zinc-300 rounded-sm text-sm"
              data-testid="new-co-date"
            />
            <input
              type="text"
              placeholder="Description"
              value={newCO.description}
              onChange={(e) => setNewCO({ ...newCO, description: e.target.value })}
              className="h-9 px-2 border border-zinc-300 rounded-sm text-sm sm:col-span-2"
              data-testid="new-co-desc"
            />
            <input
              type="number"
              placeholder="Amount"
              value={newCO.amount}
              onChange={(e) => setNewCO({ ...newCO, amount: e.target.value })}
              className="h-9 px-2 border border-zinc-300 rounded-sm text-sm font-mono"
              data-testid="new-co-amount"
            />
            <select
              value={newCO.status}
              onChange={(e) => setNewCO({ ...newCO, status: e.target.value })}
              className="h-9 px-2 border border-zinc-300 rounded-sm text-sm bg-white"
              data-testid="new-co-status"
            >
              <option value="Draft">Draft</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
            </select>
            <button
              onClick={addChangeOrder}
              className="h-9 inline-flex items-center justify-center gap-1 px-3 text-[10px] font-bold uppercase tracking-wider bg-blue-700 text-white hover:bg-blue-800 rounded-sm"
              data-testid="add-co-button"
            >
              <Plus className="w-3 h-3" /> Add
            </button>
          </div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-2">
            Approved change orders are added to Project Total on new invoices for this project.
          </div>
        </div>

        {/* Change order history */}
        {(deal.change_orders || []).length === 0 ? (
          <div className="text-sm text-zinc-500 py-3 text-center">No change orders on this project.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="change-orders-table">
              <thead>
                <tr className="border-b-2 border-zinc-950 text-left text-[10px] uppercase tracking-wider">
                  <th className="py-2 pr-3 w-32">Date</th>
                  <th className="py-2 pr-3">Description</th>
                  <th className="py-2 pr-3 w-32 text-right">Amount</th>
                  <th className="py-2 pr-3 w-28">Status</th>
                  <th className="py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {(deal.change_orders || []).map((co) => (
                  <tr key={co.id} className="border-b border-zinc-100" data-testid={`co-row-${co.id}`}>
                    <td className="py-2 pr-3 font-mono">{co.date || "—"}</td>
                    <td className="py-2 pr-3 text-zinc-700">{co.description}</td>
                    <td className="py-2 pr-3 text-right font-mono font-bold">{formatCurrency(co.amount)}</td>
                    <td className="py-2 pr-3">
                      <select
                        value={co.status || "Approved"}
                        onChange={(e) => updateChangeOrder(co.id, { status: e.target.value })}
                        className={`h-7 px-2 text-[10px] font-bold uppercase tracking-wider border rounded-sm bg-white ${
                          (co.status || "Approved") === "Approved" ? "border-emerald-300 text-emerald-700" :
                          co.status === "Rejected" ? "border-red-300 text-red-700" :
                          "border-zinc-300 text-zinc-600"
                        }`}
                      >
                        <option value="Draft">Draft</option>
                        <option value="Approved">Approved</option>
                        <option value="Rejected">Rejected</option>
                      </select>
                    </td>
                    <td className="py-2 text-right">
                      <button onClick={() => removeChangeOrder(co.id)} className="p-1.5 hover:bg-red-100 text-red-700 rounded-sm" data-testid={`del-co-${co.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
                <tr className="border-t-2 border-zinc-950 bg-zinc-50">
                  <td className="py-2 pr-3 font-bold uppercase text-[10px] tracking-wider" colSpan={2}>Approved Change Orders Total</td>
                  <td className="py-2 pr-3 text-right font-mono font-bold text-blue-700">{formatCurrency(changeOrderTotal)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Project Photos — upload, organize by album + tag, share with customer */}
      <div className="mt-6" data-section="photos">
        <ProjectPhotos dealId={id} dealTitle={deal.title} />
      </div>

      {/* Maintenance Plan */}
      <Card
        data-section="maintenance"
        title={
          <span className="inline-flex items-center gap-2">
            <Wrench className="w-3.5 h-3.5 text-blue-700" /> Maintenance Plan
          </span>
        }
        right={
          <button
            data-testid="toggle-maintenance-plan"
            onClick={() => persist({ maintenance_plan: !deal.maintenance_plan })}
            className={`inline-flex items-center gap-2 px-3 h-8 text-[10px] font-bold uppercase tracking-wider rounded-sm transition-colors ${
              deal.maintenance_plan
                ? "bg-emerald-600 text-white hover:bg-emerald-700"
                : "border border-zinc-300 text-zinc-700 hover:border-zinc-950"
            }`}
          >
            {deal.maintenance_plan ? "✓ Plan Active" : "Enable Maintenance Plan"}
          </button>
        }
      >
        {!deal.maintenance_plan ? (
          <div className="text-sm text-zinc-500 py-4">
            Click <span className="font-bold">Enable Maintenance Plan</span> to track this customer's annual maintenance, set a rate, and log yearly visits.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Annual Rate ($)</label>
                <CellInput
                  type="number"
                  value={deal.maintenance_rate}
                  onCommit={(v) => persist({ maintenance_rate: parseFloat(v || 0) })}
                  className="font-mono border-zinc-300 mt-1"
                  data-testid="maintenance-rate"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Plan Start Date</label>
                <CellInput
                  type="date"
                  value={deal.maintenance_start_date}
                  onCommit={(v) => persist({ maintenance_start_date: v })}
                  className="border-zinc-300 mt-1"
                  data-testid="maintenance-start"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Next Due (auto)</label>
                <div className="mt-1 h-9 px-2 flex items-center text-sm font-mono font-bold text-blue-700" data-testid="maintenance-next-due">
                  {deal.next_maintenance_date || "— set start date"}
                </div>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Annual Increase (%)</label>
                <CellInput
                  type="number"
                  value={deal.maintenance_escalator_pct ?? 2.5}
                  onCommit={(v) => persist({ maintenance_escalator_pct: parseFloat(v || 0) })}
                  className="font-mono border-zinc-300 mt-1"
                  data-testid="maintenance-escalator"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Rate Fixed Until</label>
                <CellInput
                  type="date"
                  value={deal.maintenance_rate_locked_until || ""}
                  onCommit={(v) => persist({ maintenance_rate_locked_until: v })}
                  className="border-zinc-300 mt-1"
                  data-testid="maintenance-rate-locked"
                />
              </div>
            </div>

            {/* Log new visit */}
            <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-3 mb-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-2">Log New Visit</div>
              <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
                <input
                  type="date"
                  value={newVisit.visit_date}
                  onChange={(e) => setNewVisit({ ...newVisit, visit_date: e.target.value })}
                  className="h-9 px-2 border border-zinc-300 rounded-sm text-sm"
                  data-testid="new-visit-date"
                />
                <input
                  type="number"
                  placeholder="Amount"
                  value={newVisit.amount}
                  onChange={(e) => setNewVisit({ ...newVisit, amount: e.target.value })}
                  className="h-9 px-2 border border-zinc-300 rounded-sm text-sm font-mono"
                  data-testid="new-visit-amount"
                />
                <select
                  value={newVisit.subcontractor_id}
                  onChange={(e) => setNewVisit({ ...newVisit, subcontractor_id: e.target.value })}
                  className="h-9 px-2 border border-zinc-300 rounded-sm text-sm bg-white"
                  data-testid="new-visit-sub"
                >
                  <option value="">— Subcontractor —</option>
                  {subcontractors.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Notes (optional)"
                  value={newVisit.notes}
                  onChange={(e) => setNewVisit({ ...newVisit, notes: e.target.value })}
                  className="h-9 px-2 border border-zinc-300 rounded-sm text-sm"
                  data-testid="new-visit-notes"
                />
                <button
                  onClick={logVisit}
                  className="h-9 inline-flex items-center justify-center gap-1 px-3 text-[10px] font-bold uppercase tracking-wider bg-blue-700 text-white hover:bg-blue-800 rounded-sm"
                  data-testid="log-visit-button"
                >
                  <Plus className="w-3 h-3" /> Log Visit
                </button>
              </div>
            </div>

            {/* Visit history */}
            {(deal.maintenance_visits || []).length === 0 ? (
              <div className="text-sm text-zinc-500 py-3 text-center">No visits logged yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="visit-history-table">
                  <thead>
                    <tr className="border-b-2 border-zinc-950 text-left text-[10px] uppercase tracking-wider">
                      <th className="py-2 pr-3 w-36">Visit Date</th>
                      <th className="py-2 pr-3 w-32 text-right">Amount</th>
                      <th className="py-2 pr-3 w-44">Subcontractor</th>
                      <th className="py-2 pr-3">Notes</th>
                      <th className="py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(deal.maintenance_visits || []).map((v) => (
                      <tr key={v.id} className="border-b border-zinc-100" data-testid={`visit-row-${v.id}`}>
                        <td className="py-2 pr-3 font-mono">{v.visit_date}</td>
                        <td className="py-2 pr-3 text-right font-mono">{formatCurrency(v.amount)}</td>
                        <td className="py-2 pr-3 text-zinc-700">{v.subcontractor_name || "—"}</td>
                        <td className="py-2 pr-3 text-zinc-700">{v.notes || "—"}</td>
                        <td className="py-2 text-right">
                          <button onClick={() => removeVisit(v.id)} className="p-1.5 hover:bg-red-100 text-red-700 rounded-sm" data-testid={`delete-visit-${v.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </Card>

      </div>

      {/* ═══ GROUP: HISTORY ═══ */}
      <div id="deal-group-history" data-testid="deal-group-history" className="scroll-mt-16">
      {/* Activity Timeline */}
      <Card
        title={
          <span className="inline-flex items-center gap-2">
            <Clock className="w-3.5 h-3.5 text-blue-700" /> Recent Activity
          </span>
        }
      >
        <div className="px-4 py-3 max-h-96 overflow-y-auto">
          <DealActivityTimeline dealId={id} />
        </div>
      </Card>

      </div>

      {emailScopeOpen && (
        <EmailScopeModal
          dealId={id}
          dealTitle={deal.title}
          dealType={deal.deal_type}
          primaryContactEmail={contact?.email || ""}
          onClose={(sent) => {
            setEmailScopeOpen(false);
            // After a successful send the backend stamps `last_scope_sent_at`
            // on the deal — refresh so the pipeline dot turns green immediately.
            if (sent) reload().catch(() => {});
          }}
        />
      )}

      {closeOutModalOpen && deal && (
        <CloseOutChecklistModal
          deal={deal}
          onClose={() => setCloseOutModalOpen(false)}
          onFinalized={() => {
            setCloseOutModalOpen(false);
            draftFinalInvoiceAfterClose();
          }}
          onOpenWcsc={() => {
            setCloseOutModalOpen(false);
            setWcscWarrantyOpen(true);
          }}
        />
      )}

      <WcscWarrantyModal
        dealId={id}
        open={wcscWarrantyOpen}
        onClose={() => setWcscWarrantyOpen(false)}
        onSaved={() => reload()}
      />

      {invoiceEditor && (
        <InvoiceEditor
          invoice={invoiceEditor}
          deals={[deal]}
          onClose={() => setInvoiceEditor(null)}
          onSaved={() => {
            setInvoiceEditor(null);
            // Refresh the deal's invoice list so the next "Record Payment"
            // click sees the new one immediately.
            api
              .get(`/invoices?deal_id=${id}`)
              .then((r) => setDealInvoices(r.data || []))
              .catch(() => {});
            toast.success("Invoice saved");
          }}
          onSendEmail={(saved) => {
            // Close the editor first, then open the email dialog so the
            // customer can be mailed without a second "Save" step.
            setInvoiceEditor(null);
            setInvoiceEmailTarget(saved);
          }}
        />
      )}

      {invoiceEmailTarget && (
        <EmailInvoiceModal
          invoice={invoiceEmailTarget}
          onClose={() => setInvoiceEmailTarget(null)}
          onSent={() => {
            setInvoiceEmailTarget(null);
            api
              .get(`/invoices?deal_id=${id}`)
              .then((r) => setDealInvoices(r.data || []))
              .catch(() => {});
            toast.success("Invoice emailed");
          }}
        />
      )}

      {scopeEditorOpen && (
        <ScopeEditorModal
          dealId={id}
          onClose={() => setScopeEditorOpen(false)}
          onSaved={() => reload().catch(() => {})}
        />
      )}

      {sendToFieldOpen && (
        <GetAppOnPhoneModal
          onClose={() => setSendToFieldOpen(false)}
          redirectPath={`/field?deal_id=${id}`}
          title="Send to Field"
          subtitle={`Scan to capture photos for ${deal?.title || "this project"}`}
        />
      )}
      {workOrderOpen && (
        <WorkOrderModal
          dealId={id}
          kind={workOrderKind}
          onClose={() => setWorkOrderOpen(false)}
          onSent={(res) => {
            const label = workOrderKind === "change-order" ? "Change order" : "Work order";
            toast.success(res?.email_sent
              ? `${label} emailed to the sub`
              : `${label} saved (email could not be sent — check SMTP)`);
            setWorkOrderOpen(false);
            reload();
            loadWorkOrders();
          }}
        />
      )}
    </div>
  );
}

const Card = ({ title, right, children, ...rest }) => (
  <div className="bg-white border border-zinc-200 rounded-sm p-6 mb-6" {...rest}>
    <div className="flex items-center justify-between mb-4 pb-3 border-b border-zinc-100 gap-3 flex-wrap">
      <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">{title}</div>
      {right}
    </div>
    <div>{children}</div>
  </div>
);

const Row = ({ label, value, bold, highlight, accent }) => (
  <div className={`flex items-center justify-between py-2 ${highlight ? "bg-blue-50 -mx-2 px-2 rounded-sm" : ""}`}>
    <div className="text-xs uppercase tracking-wider text-zinc-500">{label}</div>
    <div className={`text-sm ${bold ? "font-bold text-zinc-950" : "text-zinc-700"} font-mono ${accent || ""}`}>{value || "—"}</div>
  </div>
);

const KpiCard = ({ label, value, hint, testId, accent }) => (
  <div className="bg-white border border-zinc-200 p-6 rounded-sm" data-testid={testId}>
    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-3">{label}</div>
    <div className={`font-heading text-3xl font-black tracking-tighter ${accent || "text-zinc-950"}`}>{value}</div>
    {hint && <div className="text-xs text-zinc-500 mt-2">{hint}</div>}
  </div>
);

const Mini = ({ label, value }) => (
  <div className="border border-zinc-200 rounded-sm px-3 py-2">
    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
    <div className="text-sm font-bold font-mono">{value}</div>
  </div>
);

const CellInput = ({ value, onCommit, type = "text", className = "", disabled, ...rest }) => {
  const [v, setV] = useState(value ?? "");
  // When the underlying value changes externally (other user, server sync) but the
  // user has unsaved local edits, commit the local value FIRST so we don't lose typing.
  useEffect(() => {
    if (String(v) !== String(value ?? "") && document.activeElement?.dataset?.testid !== rest["data-testid"]) {
      // External update + we don't have focus → safe to sync down
      setV(value ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <input
      type={type}
      value={v}
      disabled={disabled}
      onChange={(e) => {
        const next = e.target.value;
        setV(next);
        // For numeric cells, commit on every keystroke so a parent re-render
        // (e.g., a sibling "Add Row" click) doesn't wipe unsaved input.
        if (type === "number" && String(next) !== String(value ?? "")) {
          onCommit(next);
        }
      }}
      onBlur={() => { if (String(v) !== String(value ?? "")) onCommit(v); }}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      className={`w-full h-9 px-2 border border-transparent hover:border-zinc-300 focus:border-blue-700 focus:outline-none bg-transparent rounded-sm text-sm ${className} ${disabled ? "opacity-50" : ""}`}
      {...rest}
    />
  );
};

const CellSelect = ({ value, onCommit, options = [], className = "", ...rest }) => (
  <select
    value={value ?? ""}
    onChange={(e) => onCommit(e.target.value)}
    className={`w-full h-9 px-2 border border-transparent hover:border-zinc-300 focus:border-blue-700 focus:outline-none bg-transparent rounded-sm text-sm ${className}`}
    {...rest}
  >
    {options.map((o) => (
      <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
    ))}
  </select>
);


function EmailScopeModal({ dealId, dealTitle, dealType, primaryContactEmail, onClose }) {
  const [to, setTo] = useState(primaryContactEmail || "");
  const [cc, setCc] = useState("");
  const [message, setMessage] = useState("");
  const [aliases, setAliases] = useState([]);
  const [fromEmail, setFromEmail] = useState("");
  const [taxonomy, setTaxonomy] = useState([]);
  const [libFiles, setLibFiles] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [filterCat, setFilterCat] = useState("");
  const [sending, setSending] = useState(false);
  const [smartMatches, setSmartMatches] = useState({ ids: new Set(), reasons: {}, tokens: [] });
  const [smartApplied, setSmartApplied] = useState(false);
  const [coverPhotos, setCoverPhotos] = useState([]); // [{id, signed_url, display_name, is_cover}]
  const [selectedPhotoIds, setSelectedPhotoIds] = useState([]); // ids of project photos to attach

  const isAssessment = (dealType || "").toLowerCase() === "assessment";
  const docKind = isAssessment ? "assessment" : "scope";

  useEffect(() => {
    api.get("/email-aliases").then((r) => {
      setAliases(r.data?.aliases || []);
      const docDefault = (r.data?.defaults || {})[docKind];
      setFromEmail(docDefault || r.data?.default || "");
    }).catch(() => {});
    api.get("/library/taxonomy").then((r) => setTaxonomy(r.data?.taxonomy || [])).catch(() => {});
    api.get("/library/files").then((r) => {
      // Hide financial/legal documents from the scope email modal entirely
      // — this is a customer-facing proposal blast, not an internal packet.
      // Anything under "Books" (accounting/financial), "Contracts & Legal"
      // (MSAs, T&C, Change Orders, Lien Waivers), or the "W-9" subcategory
      // carries pricing / tax / legal data that should never travel with
      // a proposal. Everything else (specs, brochures, SDS, COIs, licenses,
      // certs) stays available.
      const _EXCLUDED_CATEGORIES = new Set(["Books", "Contracts & Legal"]);
      const _EXCLUDED_SUBCATS = new Set(["W-9"]);
      const safe = (r.data || []).filter((f) => {
        if (_EXCLUDED_CATEGORIES.has(f.category)) return false;
        if (_EXCLUDED_SUBCATS.has(f.subcategory)) return false;
        return true;
      });
      setLibFiles(safe);
    }).catch(() => {});
    // Pull smart suggestions for this deal — highlight matches (sort to top +
    // "SMART" badge) but do NOT auto-check them. Rep must explicitly opt in
    // to each attachment before it goes to the customer.
    api.get(`/deals/${dealId}/scope-suggestions`).then((r) => {
      const ids = new Set(r.data?.file_ids || []);
      setSmartMatches({ ids, reasons: r.data?.reasons || {}, tokens: r.data?.tokens || [] });
      // Intentionally leaving selectedIds empty — attachments default OFF.
      setSmartApplied(false);
    }).catch(() => {});
    // Fetch project photos so the cover-photo attachment is available if
    // the rep opts in. Per user request (2026-02), no attachments default
    // on in this modal — everything must be checked manually.
    api.get(`/projects/${dealId}/photos`).then((r) => {
      const all = r.data || [];
      const covers = all.filter((p) => p.is_cover);
      const candidates = covers.length > 0 ? covers : all.slice(0, 1);
      setCoverPhotos(candidates);
      setSelectedPhotoIds([]);
    }).catch(() => setCoverPhotos([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docKind, dealId]);

  const toggleId = (id) => setSelectedIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const clearSmart = () => {
    setSelectedIds((s) => s.filter((id) => !smartMatches.ids.has(id)));
    setSmartApplied(false);
  };
  const reapplySmart = () => {
    setSelectedIds((s) => Array.from(new Set([...s, ...Array.from(smartMatches.ids)])));
    setSmartApplied(true);
  };

  const filtered = useMemo(() => {
    const base = filterCat ? libFiles.filter((f) => f.category === filterCat) : libFiles;
    // Sort: smart matches first, then by display_name
    return [...base].sort((a, b) => {
      const am = smartMatches.ids.has(a.id) ? 0 : 1;
      const bm = smartMatches.ids.has(b.id) ? 0 : 1;
      if (am !== bm) return am - bm;
      return (a.display_name || "").localeCompare(b.display_name || "");
    });
  }, [libFiles, filterCat, smartMatches]);

  const send = async () => {
    if (!to.trim()) { toast.error("Recipient email required"); return; }
    setSending(true);
    try {
      const r = await api.post(`/deals/${dealId}/spec-sheet/email`, {
        to_email: to.trim(),
        cc_email: cc.trim(),
        from_email: fromEmail,
        message: message.trim(),
        library_file_ids: selectedIds,
        cover_photo_ids: selectedPhotoIds,
      });
      toast.success(r.data?.message || "Scope emailed");
      onClose(true);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" data-testid="email-scope-modal">
      <div className="bg-white border border-zinc-200 rounded-sm w-full max-w-3xl my-8">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-blue-700 mb-1">
              Email {isAssessment ? "Assessment" : "Scope"}
            </div>
            <h3 className="font-heading text-xl font-black tracking-tight">{dealTitle}</h3>
            <div className="text-xs text-zinc-500 mt-1">
              {isAssessment ? "Assessment" : "Scope"} PDF is auto-attached. Add Library docs (brochures, specs, certs, contracts) as additional attachments.
              <span className="ml-1 text-zinc-400">Sending from <span className="font-mono text-zinc-700">{fromEmail || "—"}</span>.</span>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-zinc-500 hover:text-zinc-950 text-xs uppercase tracking-wider font-bold">Close</button>
        </div>
        <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Left — email composition */}
          <div className="space-y-3">
            {aliases.length > 1 && (
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-600 mb-1">From</label>
                <select value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} className="w-full h-10 px-3 border border-zinc-300 rounded-sm text-sm bg-white" data-testid="scope-from-email">
                  {aliases.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-600 mb-1">To *</label>
              <input value={to} onChange={(e) => setTo(e.target.value)} className="w-full h-10 px-3 border border-zinc-300 rounded-sm text-sm" data-testid="scope-to-email" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-600 mb-1">CC</label>
              <input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="(optional)" className="w-full h-10 px-3 border border-zinc-300 rounded-sm text-sm" data-testid="scope-cc-email" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1 gap-2">
                <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-600">Custom Message (optional)</label>
                <GrammarCheck text={message || ""} onChange={setMessage} label="Check Grammar" />
              </div>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} placeholder="Override the default email body. Leave blank for the standard proposal blurb." className="w-full px-3 py-2 border border-zinc-300 rounded-sm text-sm" data-testid="scope-message" />
            </div>
          </div>

          {/* Right — library picker */}
          <div>
            {/* Cover photo picker removed 2026-02: the cover photo is already
                embedded on Page 1 of the scope PDF, so attaching it as a
                separate JPG created duplicate images in the customer's inbox. */}

            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Library Attachments ({selectedIds.length} selected)</div>
              <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} className="h-7 px-2 border border-zinc-300 rounded-sm text-xs bg-white" data-testid="scope-lib-filter">
                <option value="">All categories</option>
                {taxonomy.map((c) => <option key={c.category} value={c.category}>{c.category}</option>)}
              </select>
            </div>
            {smartMatches.ids.size > 0 && (
              <div className="mb-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-sm flex items-center justify-between gap-2 text-[11px]" data-testid="smart-pick-badge">
                <div className="text-blue-900">
                  <span className="font-bold">✨ Smart-picked {smartMatches.ids.size} doc{smartMatches.ids.size === 1 ? "" : "s"}</span>
                  <span className="text-blue-700"> for {smartMatches.tokens.filter((t) => t !== "general").slice(0, 3).join(", ") || "this scope"}</span>
                </div>
                <button
                  type="button"
                  onClick={smartApplied ? clearSmart : reapplySmart}
                  className="text-blue-700 hover:text-blue-900 font-bold uppercase tracking-wider text-[10px]"
                  data-testid="smart-pick-toggle"
                >
                  {smartApplied ? "Clear" : "Re-apply"}
                </button>
              </div>
            )}
            <div className="border border-zinc-200 rounded-sm max-h-72 overflow-y-auto">
              {filtered.length === 0 ? (
                <div className="text-center text-xs text-zinc-500 py-8">No library docs match.</div>
              ) : filtered.map((f) => {
                const isSmart = smartMatches.ids.has(f.id);
                return (
                  <label key={f.id} className={`flex items-start gap-2 px-3 py-2 border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer ${isSmart ? "bg-blue-50/40" : ""}`} data-testid={`scope-lib-${f.id}`}>
                    <input type="checkbox" checked={selectedIds.includes(f.id)} onChange={() => toggleId(f.id)} className="mt-1" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <div className="text-xs font-bold truncate">{f.display_name}</div>
                        {isSmart && <span className="inline-block px-1.5 py-px text-[8px] font-bold uppercase tracking-wider bg-blue-700 text-white rounded-sm whitespace-nowrap" title={(smartMatches.reasons[f.id] || []).join(", ")}>Smart</span>}
                      </div>
                      <div className="text-[10px] text-zinc-500 truncate">{f.category} / {f.subcategory}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-zinc-200 flex justify-between items-center gap-2">
          <div className="text-[11px] text-zinc-500">
            Will send <b>scope PDF</b>
            {" "}+ <b>{selectedIds.length}</b> library doc{selectedIds.length === 1 ? "" : "s"} = <b>{selectedIds.length + 1}</b> total attachment{(selectedIds.length + 1) === 1 ? "" : "s"}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-4 h-10 text-xs font-bold uppercase tracking-wider border border-zinc-300 rounded-sm hover:bg-zinc-50">Cancel</button>
            <button type="button" onClick={send} disabled={sending} className="inline-flex items-center gap-2 bg-blue-700 text-white px-4 h-10 text-xs font-bold uppercase tracking-wider hover:bg-blue-800 disabled:opacity-50 rounded-sm" data-testid="scope-send"><Mail className="w-4 h-4" /> {sending ? "Sending..." : "Send Scope"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * WorkOrderModal — issues a Work Order to the subcontractor.
 *
 * Flow: GET draft from backend (auto-fills sub info, project, scope bullets
 * from the same spec sheet the customer sees) → rep edits any field →
 * Preview (PDF) OR Send. Sending persists the WO, attaches the spec sheet,
 * emails the sub with a public sign link, and toasts back.
 */
function WorkOrderModal({ dealId, onClose, onSent, kind = "work-order" }) {
  const isCO = kind === "change-order";
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({});
  const [existing, setExisting] = useState(null);
  // Subcontractor roster — drives the "Contractor" dropdown so the rep can
  // pick a vendor and auto-fill all sub fields in one shot.
  const [subs, setSubs] = useState([]);
  // Western Colloid manufacturer specs from the Library — picker lets the rep
  // attach one or more to the outbound WO email AND drop a reference line
  // into the description so the sub knows which spec governs the job.
  // Change Orders skip this picker entirely (no spec attached to a CO).
  const [librarySpecs, setLibrarySpecs] = useState([]);
  const [selectedLibraryIds, setSelectedLibraryIds] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const reqs = [
          api.get(`/deals/${dealId}/work-order/draft`, { params: { kind } }),
          api.get("/vendors?kind=Subcontractor").catch(() => ({ data: [] })),
        ];
        if (!isCO) {
          reqs.push(api.get("/library/files?category=Western%20Colloid&subcategory=Specifications").catch(() => ({ data: [] })));
        }
        const [draftR, subsR, libR] = await Promise.all(reqs);
        setForm(draftR.data?.existing ? { ...draftR.data.draft, ...draftR.data.existing } : draftR.data?.draft || {});
        setExisting(draftR.data?.existing || null);
        setSubs((subsR.data || []).slice().sort((a, b) => (a.name || "").localeCompare(b.name || "")));
        if (libR) {
          setLibrarySpecs((libR.data || []).slice().sort((a, b) => (a.display_name || a.original_filename || "").localeCompare(b.display_name || b.original_filename || "")));
          setSelectedLibraryIds(draftR.data?.existing?.library_file_ids || []);
        }
      } catch (e) {
        const status = e?.response?.status;
        if (status === 404) {
          toast.error("This deal can't be found — it may have been deleted. Refresh the page.", { duration: 8000 });
          onClose?.();
        } else {
          toast.error(e?.response?.data?.detail || e.message);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [dealId, kind]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Toggle a Library spec into/out of the attachment list. We key off the
  // file id so the checkbox state stays in sync after reloads.
  const toggleLibraryFile = (lf) => {
    setSelectedLibraryIds((prev) =>
      prev.includes(lf.id) ? prev.filter((x) => x !== lf.id) : [...prev, lf.id]
    );
  };

  // Picking a sub from the dropdown auto-fills every field that came from the
  // vendor record — Contractor name, company, address, contact, email. Rep
  // can still edit any of them after.
  const pickSub = (subId) => {
    if (!subId) { set("contractor", ""); return; }
    const sub = subs.find((x) => x.id === subId);
    if (!sub) return;
    setForm((p) => ({
      ...p,
      contractor: sub.name || "",
      sub_company: sub.name || "",
      sub_address: sub.address || "",
      sub_contact: sub.contact_name || "",
      sub_email: sub.email || "",
    }));
  };

  const preview = async () => {
    try {
      const r = await api.post(`/deals/${dealId}/work-order/preview`, { ...form, kind, library_file_ids: selectedLibraryIds }, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const send = async () => {
    if (!form.sub_email || !form.sub_email.includes("@")) {
      toast.error("Subcontractor email is required"); return;
    }
    setSubmitting(true);
    try {
      const r = await api.post(`/deals/${dealId}/work-order/send`, { ...form, kind, library_file_ids: selectedLibraryIds });
      onSent?.(r.data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 404) {
        toast.error("This deal can't be found in the database — it may have been deleted. Refresh the page and try again.", { duration: 8000 });
      } else if (detail) {
        toast.error(typeof detail === "string" ? detail : JSON.stringify(detail));
      } else {
        toast.error(`Work order send failed (HTTP ${status || "?"}) — ${e.message}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="work-order-modal">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <div className={`text-[10px] font-bold uppercase tracking-[0.2em] ${isCO ? "text-amber-700" : "text-blue-700"}`}>
              {isCO ? "Issue Change Order" : "Issue Work Order"}
            </div>
            <h2 className="font-heading text-xl font-black">
              {isCO ? "Send Change Order to Subcontractor" : "Send Work Order to Subcontractor"}
            </h2>
            {isCO && (
              <div className="text-[11px] text-amber-700 mt-1">
                A Change Order amends the previously executed Work Order. Stage will NOT change on signature. Spec sheet is NOT attached.
              </div>
            )}
            {existing && (
              <div className="text-[11px] text-amber-700 mt-1">
                ⚠ A {isCO ? "change order" : "work order"} already exists for this deal. Saving will overwrite it
                {existing.signed_at && " — but the existing signature will NOT be invalidated; the sub will need to sign the new version."}
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700 text-xl leading-none px-2">×</button>
        </div>
        {loading ? (
          <div className="p-8 text-center text-zinc-500 text-sm">Loading draft…</div>
        ) : (
          <div className="p-5 space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <Field label="WO Date" value={form.wo_date} onChange={(v) => set("wo_date", v)} testId="wo-date" />
              <Field label="Work Date" value={form.work_date} onChange={(v) => set("work_date", v)} testId="wo-work-date" />
            </div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mt-2">Subcontractor</div>
            <Field label="Company Name" value={form.sub_company} onChange={(v) => set("sub_company", v)} testId="wo-sub-company" />
            <Field label="Company Address" value={form.sub_address} onChange={(v) => set("sub_address", v)} testId="wo-sub-address" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Contact Name" value={form.sub_contact} onChange={(v) => set("sub_contact", v)} testId="wo-sub-contact" />
              <Field label="Email *" value={form.sub_email} onChange={(v) => set("sub_email", v)} testId="wo-sub-email" required />
            </div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mt-2">Project</div>
            <Field label="Project Name" value={form.project_name} onChange={(v) => set("project_name", v)} testId="wo-project-name" />
            <Field label="Project Address" value={form.project_address} onChange={(v) => set("project_address", v)} testId="wo-project-address" />
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-600 mb-1">Contractor (entity SealTech is paying)</label>
              <select
                value={subs.find((s) => s.name === form.contractor)?.id || ""}
                onChange={(e) => pickSub(e.target.value)}
                data-testid="wo-contractor-select"
                className="w-full h-10 px-3 border border-zinc-300 rounded-sm text-sm bg-white"
              >
                <option value="">— Pick a subcontractor —</option>
                {subs.map((sub) => (
                  <option key={sub.id} value={sub.id}>
                    {sub.name}{sub.contact_name ? ` · ${sub.contact_name}` : ""}
                  </option>
                ))}
              </select>
              <div className="text-[10px] text-zinc-500 mt-1">Picking a sub auto-fills the company name, address, contact, and email below. Edit any field after.</div>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-600 mb-1">
                {isCO ? "Change Order Description (what's changing)" : "Description (scope of work)"}
              </label>
              <textarea
                value={form.description || ""}
                onChange={(e) => set("description", e.target.value)}
                rows={isCO ? 8 : 14}
                data-testid="wo-description"
                placeholder={isCO ? "e.g. ADD: 800 SF of additional silicone at the south wing roof — +$7,800.00.\nREMOVE: Walk-pad bundle near AC unit (relocated by GC) — -$650.00." : ""}
                className="w-full px-3 py-2 border border-zinc-300 rounded-sm text-xs font-mono"
              />
              <div className="text-[10px] text-zinc-500 mt-1">
                {isCO
                  ? "Type just the delta — added/removed scope and net price change. HTML tags <b>&lt;b&gt;</b>, <b>&lt;br/&gt;</b> supported."
                  : <>Edit freely — the box on the PDF auto-grows to push the signatures to the bottom of the page. HTML tags <b>&lt;b&gt;</b>, <b>&lt;br/&gt;</b> supported.</>}
              </div>
            </div>
            {/* Library spec picker — pulls PDFs uploaded under
                Library → Western Colloid → Specifications. Ticked specs are
                attached to the outbound email AND referenced in the
                description. Change Orders skip this picker entirely. */}
            {!isCO && (
            <div className="border border-zinc-200 rounded-sm">
              <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200 text-[10px] font-bold uppercase tracking-wider text-zinc-600">
                Attach Manufacturer Specs <span className="text-zinc-400 normal-case font-normal">(Library → Western Colloid → Specifications)</span>
              </div>
              <div className="p-3 max-h-40 overflow-y-auto space-y-1">
                {librarySpecs.length === 0 && (
                  <div className="text-[11px] text-zinc-500 italic">No specs in Library yet. Upload PDFs under Library → Western Colloid → Specifications to make them available here.</div>
                )}
                {librarySpecs.map((lf) => (
                  <label key={lf.id} className="flex items-start gap-2 text-xs cursor-pointer hover:bg-zinc-50 px-2 py-1 rounded-sm">
                    <input type="checkbox" checked={selectedLibraryIds.includes(lf.id)} onChange={() => toggleLibraryFile(lf)} data-testid={`wo-lib-${lf.id}`} className="mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-zinc-900 truncate">{lf.display_name || lf.original_filename}</div>
                      {lf.description && <div className="text-[10px] text-zinc-500 truncate">{lf.description}</div>}
                    </div>
                  </label>
                ))}
              </div>
              {selectedLibraryIds.length > 0 ? (
                <div className="px-3 py-2 bg-blue-50 border-t border-blue-200 text-[11px] text-blue-900">
                  <b>{selectedLibraryIds.length}</b> manufacturer spec{selectedLibraryIds.length === 1 ? "" : "s"} will be attached. The auto-generated SealTech Spec Sheet is <b>replaced</b> by your selection — sub gets one scope packet, not two.
                </div>
              ) : (
                <div className="px-3 py-2 bg-amber-50 border-t border-amber-200 text-[11px] text-amber-900">
                  No manufacturer spec attached → the email will include the auto-generated <b>SealTech Spec Sheet</b> (Silicone Roof Scope template) as the scope reference.
                </div>
              )}
            </div>
            )}
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-600 mb-1">Total ($)</label>
              <input type="number" step="0.01" value={form.total || 0} onChange={(e) => set("total", Number(e.target.value))} data-testid="wo-total" className="w-48 h-10 px-3 border border-zinc-300 rounded-sm text-sm font-mono text-right" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-600 mb-1">Notes (override default Master-Subcontractor text)</label>
              <textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={3} placeholder={isCO ? "" : "Leave blank to use the standard Work Order language (subcontractor agrees to perform the Work per manufacturer specs, furnishes labor/materials/insurance/supervision/equipment, etc.)"} data-testid="wo-notes" className="w-full px-3 py-2 border border-zinc-300 rounded-sm text-xs" />
            </div>
            <div className={`${isCO ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-blue-50 border-blue-200 text-blue-900"} border rounded-sm p-3 text-[11px]`}>
              <b>Email attachments:</b> {isCO
                ? "Change Order PDF only (no spec sheet — this amends the original Work Order)."
                : <>Work Order PDF + {selectedLibraryIds.length > 0
                  ? `${selectedLibraryIds.length} Manufacturer Spec${selectedLibraryIds.length === 1 ? "" : "s"} (auto SealTech Spec Sheet is replaced)`
                  : "the auto-generated SealTech Spec Sheet"}.</>}
            </div>
          </div>
        )}
        <div className="px-5 py-4 border-t border-zinc-200 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 h-10 text-xs font-bold uppercase tracking-wider border border-zinc-300 rounded-sm hover:bg-zinc-50">Cancel</button>
          <button type="button" onClick={preview} disabled={loading} data-testid="wo-preview" className="px-4 h-10 text-xs font-bold uppercase tracking-wider border border-blue-700 text-blue-700 hover:bg-blue-50 rounded-sm disabled:opacity-50">Preview PDF</button>
          <button type="button" onClick={send} disabled={loading || submitting} data-testid="wo-send" className={`px-4 h-10 text-xs font-bold uppercase tracking-wider text-white rounded-sm disabled:opacity-50 ${isCO ? "bg-amber-600 hover:bg-amber-700" : "bg-blue-700 hover:bg-blue-800"}`}>{submitting ? "Sending…" : (isCO ? "Send Change Order to Sub" : "Send to Subcontractor")}</button>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, testId, required }) => (
  <div>
    <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-600 mb-1">{label}</label>
    <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} required={required} className="w-full h-10 px-3 border border-zinc-300 rounded-sm text-sm" />
  </div>
);


/** ActualLaborCard — burdened labor + field expenses from approved Time Clock
 * punches, shown next to milestones so estimate vs actual is one glance. */
function CrewAssignCard({ deal }) {
  const [emps, setEmps] = React.useState(null);
  const [ids, setIds] = React.useState(deal.crew_ids || []);
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => { setIds(deal.crew_ids || []); }, [deal.crew_ids]);
  React.useEffect(() => {
    if ((open || ids.length > 0) && emps === null) {
      api.get("/employees").then((r) => setEmps((r.data || []).filter((e) => e.is_active !== false))).catch(() => setEmps([]));
    }
  }, [open, ids.length, emps]);
  const toggle = async (eid) => {
    const next = ids.includes(eid) ? ids.filter((x) => x !== eid) : [...ids, eid];
    setIds(next);
    try { await api.patch(`/deals/${deal.id}/crew`, { crew_ids: next }); } catch { toast.error("Could not save crew"); }
  };
  const names = (emps || []).filter((e) => ids.includes(e.id)).map((e) => e.name);
  return (
    <div className="flex flex-wrap items-center gap-2 border border-zinc-200 bg-white rounded-sm px-3 py-2 mb-6" data-testid="deal-crew-card">
      <HardHat className="w-4 h-4 text-zinc-400 shrink-0" />
      <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Crew</span>
      {ids.length === 0 && !open && <span className="text-xs text-zinc-400">none assigned</span>}
      {!open && ids.length > 0 && (
        <span className="text-xs font-bold" data-testid="deal-crew-names">{names.length > 0 ? names.join(", ") : `${ids.length} assigned`}</span>
      )}
      {open && (emps || []).map((e) => (
        <label key={e.id} className={`inline-flex items-center gap-1 text-[11px] font-bold border rounded-sm px-2 py-1 cursor-pointer ${ids.includes(e.id) ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 hover:border-zinc-950"}`} data-testid={`crew-toggle-${e.id}`}>
          <input type="checkbox" className="hidden" checked={ids.includes(e.id)} onChange={() => toggle(e.id)} />
          {e.name}
        </label>
      ))}
      {open && emps === null && <span className="text-xs text-zinc-400">loading…</span>}
      <button onClick={() => setOpen(!open)} className="ml-auto h-7 px-2.5 text-[10px] font-bold uppercase tracking-wider border border-zinc-300 rounded-sm hover:border-zinc-950" data-testid="deal-crew-edit">
        {open ? "Done" : "Assign Crew"}
      </button>
    </div>
  );
}

function FollowUpCard({ deal, onSaved }) {
  const [date, setDate] = React.useState(deal.next_followup_date || "");
  const [note, setNote] = React.useState(deal.next_followup_note || "");
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    setDate(deal.next_followup_date || "");
    setNote(deal.next_followup_note || "");
  }, [deal.next_followup_date, deal.next_followup_note]);
  const save = async (d, n) => {
    setBusy(true);
    try {
      await api.put(`/deals/${deal.id}`, { ...deal, next_followup_date: d, next_followup_note: n });
      toast.success(d ? "Follow-up set" : "Follow-up cleared");
      onSaved && onSaved();
    } catch (e) { toast.error("Could not save follow-up"); }
    setBusy(false);
  };
  const overdue = date && date <= new Date().toISOString().slice(0, 10);
  return (
    <div className={`flex flex-wrap items-center gap-2 border rounded-sm px-3 py-2 mb-6 ${overdue ? "border-amber-400 bg-amber-50" : "border-zinc-200 bg-white"}`} data-testid="deal-followup-card">
      <Bell className={`w-4 h-4 shrink-0 ${overdue ? "text-amber-600" : "text-zinc-400"}`} />
      <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Next follow-up</span>
      <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-8 px-2 border border-zinc-300 rounded-sm text-xs bg-white" data-testid="followup-date" />
      <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="What's the plan? (call, email…)" className="h-8 px-2 border border-zinc-300 rounded-sm text-xs bg-white flex-1 min-w-[160px]" data-testid="followup-note" />
      <button disabled={busy || !date} onClick={() => save(date, note)} className="h-8 px-3 text-[10px] font-bold uppercase tracking-wider bg-zinc-950 text-white rounded-sm disabled:opacity-40 hover:bg-zinc-800" data-testid="followup-save">Set</button>
      {deal.next_followup_date && (
        <button disabled={busy} onClick={() => { setDate(""); setNote(""); save("", ""); }} className="h-8 px-2 text-[10px] font-bold uppercase tracking-wider border border-zinc-300 rounded-sm hover:border-zinc-500" data-testid="followup-clear">Done / Clear</button>
      )}
      {overdue && deal.next_followup_date && <span className="text-[10px] font-bold text-amber-700 uppercase tracking-wider" data-testid="followup-overdue">Due</span>}
    </div>
  );
}

function ActualLaborCard({ dealId, estLabor = 0 }) {
  const [lc, setLc] = React.useState(null);
  React.useEffect(() => {
    api.get(`/timeclock/labor-cost/${dealId}`).then((r) => setLc(r.data)).catch(() => {});
  }, [dealId]);
  if (!lc || (lc.total_hours === 0 && lc.expense_cost === 0)) return null;
  const variance = estLabor > 0 ? lc.labor_cost - estLabor : 0;
  return (
    <Card title="Actual Labor (Time Clock)" data-section="actual-labor">
      <div className="grid grid-cols-3 gap-3 text-center mb-3" data-testid="actual-labor-card">
        <div className="border border-zinc-200 rounded-sm p-2">
          <div className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">Approved Hours</div>
          <div className="text-lg font-black">{lc.total_hours}</div>
        </div>
        <div className="border border-zinc-200 rounded-sm p-2">
          <div className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">Burdened Labor</div>
          <div className="text-lg font-black text-red-700">{formatCurrency(lc.labor_cost)}</div>
        </div>
        <div className="border border-zinc-200 rounded-sm p-2">
          <div className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">Field Expenses</div>
          <div className="text-lg font-black text-red-700">{formatCurrency(lc.expense_cost)}</div>
        </div>
      </div>
      {estLabor > 0 && (
        <div className="flex items-center justify-between border border-zinc-200 rounded-sm px-3 py-2 mb-3 text-xs" data-testid="labor-est-vs-actual">
          <span className="font-bold uppercase tracking-wider text-zinc-500 text-[9px]">Est. vs Actual Labor</span>
          <span className="font-mono">
            {formatCurrency(estLabor)} est · {formatCurrency(lc.labor_cost)} actual ·{" "}
            <span className={`font-bold ${variance > 0 ? "text-red-700" : "text-emerald-700"}`}>
              {variance > 0 ? "+" : ""}{formatCurrency(variance)} {variance > 0 ? "over" : "under"}
            </span>
          </span>
        </div>
      )}
      <div className="text-xs space-y-1">
        {lc.by_employee.map((b) => (
          <div key={b.name} className="flex justify-between text-zinc-600">
            <span>{b.name}{b.missing_rate && <span className="text-amber-600 font-bold text-[9px] uppercase ml-1">no pay rate set</span>}</span>
            <span className="font-mono">{b.hours}h · {formatCurrency(b.cost)}</span>
          </div>
        ))}
      </div>
      <div className="text-[10px] text-zinc-400 mt-2">Wage × {lc.burden_multiplier} burden (taxes, comp, insurance). Set pay rates on the Employee record.</div>
    </Card>
  );
}

